import { useState, useEffect, useRef, useMemo, Fragment, Component, type ReactNode, type ErrorInfo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useIsExperimental } from "@/hooks/useIsExperimental";
import { useQuery as useRQQuery } from "@tanstack/react-query";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { SideChat } from "@/components/SideChat";
import { format } from "date-fns";
import { Link, useLocation, useRoute } from "wouter";
import { 
  Stethoscope, 
  UserRound,
  LogOut, 
  Plus, 
  FileText, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ChevronRight,
  Calendar,
  User,
  Loader2,
  Mic,
  LayoutDashboard,
  MessageSquare,
  Pencil,
  Square,
  MoreVertical,
  Trash2,
  Sparkles,
  Copy,
  Check,
  ArrowLeft,
  RefreshCw,
  Search,
  X,
  GitCompare,
  GraduationCap,
  ThumbsUp,
  ThumbsDown,
  Globe,
  Code,
  History,
  Shield,
  FileDown,
  Users,
  Settings as SettingsIcon,
  FlaskConical
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { applyStagedEdits } from "@/lib/stagedSectionApply";
import {
  createGatewaySession as audioApiCreateSession,
  startRecordingSession as audioApiStart,
  uploadChunk as audioApiUploadChunk,
  completeRecording as audioApiComplete,
  getRecordingStatus as audioApiGetStatus,
  getSessionTranscript as audioApiGetSessionTranscript,
} from "@/lib/audioApiClient";
import { DocumentSectionHistoryPanel } from "@/components/DocumentSectionHistoryPanel";
import { LearnFromEditsIndicator } from "@/components/LearnFromEditsIndicator";
import { SpeakerAttributedTranscript } from "@/components/SpeakerAttributedTranscript";
import { SpeakerAttributionViewer } from "@/components/SpeakerAttributionViewer";
import {
  SessionViewLogger,
  ShareSessionDialog,
  AuditViewerDialog,
  SharedWithMeList,
} from "@/components/TeamSessionTools";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { SectionGenerationProgress } from "@/components/section-generation-progress";
import { runWithProgress, useSectionProgress, RunError, type ProgressStage } from "@/hooks/use-section-progress";
import ReactMarkdown from "react-markdown";
import { renderToStaticMarkup } from "react-dom/server";
import remarkGfm from "remark-gfm";
import { useRecordingStatus } from "@/contexts/recording-context";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";
import { resolveEffectiveModel } from "@shared/modelResolution";
import { normalizeTier } from "@shared/llmProviders";
import { ExperimentalRecorder } from "@/components/ExperimentalRecorder";

type RecordingSession = {
  _id: Id<"recordingSessions">;
  userId: Id<"users">;
  patientName: string;
  gender?: "male" | "female";
  title: string;
  recordingDate: number;
  audioUrl?: string;
  audioData?: string;
  audioMimeType?: string;
  audioApiRecordingId?: string;
  gatewaySessionId?: string;
  transcription?: string;
  generatedDocument?: string;
  documentGeneratedAt?: number;
  modelUsed?: string;
  status: "pending" | "recording" | "processing" | "completed" | "failed";
  duration?: number;
  notes?: string;
  templateId?: Id<"templates">;
  speakerAttributionStatus?: "processing" | "done" | "failed";
  speakerAttributionHash?: string;
  speakerAttribution?: string;
};

function StatusBadge({ 
  status, 
  hasTranscription, 
  hasGeneratedDocument 
}: { 
  status: RecordingSession["status"]; 
  hasTranscription?: boolean;
  hasGeneratedDocument?: boolean;
}) {
  let displayStatus: string;
  let displayConfig: { icon: typeof Clock; bg: string; text: string };
  
  const config = {
    pending: { icon: Clock, bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-600 dark:text-amber-400" },
    processing: { icon: Loader2, bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-600 dark:text-blue-400" },
    transcription: { icon: FileText, bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-600 dark:text-purple-400" },
    completed: { icon: CheckCircle2, bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-600 dark:text-green-400" },
    failed: { icon: AlertCircle, bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-600 dark:text-red-400" },
  };
  
  if (hasGeneratedDocument) {
    displayStatus = "Completed";
    displayConfig = config.completed;
  } else if (status === "failed") {
    displayStatus = "Failed";
    displayConfig = config.failed;
  } else if (status === "processing") {
    displayStatus = "Transcribing...";
    displayConfig = config.processing;
  } else if (hasTranscription) {
    displayStatus = "Transcription";
    displayConfig = config.transcription;
  } else {
    displayStatus = "Pending";
    displayConfig = config.pending;
  }
  
  const { icon: Icon, bg, text } = displayConfig;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className={`w-3 h-3 ${status === "processing" ? "animate-spin" : ""}`} />
      {displayStatus}
    </span>
  );
}

function AudioRecorder({ 
  sessionId, 
  userId,
  existingTranscription,
  onTranscriptionComplete,
  onEndSession
}: { 
  sessionId: Id<"recordingSessions">; 
  userId: Id<"users">;
  existingTranscription?: string;
  sessionStatus: RecordingSession["status"];
  onTranscriptionComplete: () => void;
  onEndSession?: () => void;
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState("");
  const [showStopModal, setShowStopModal] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const isInIframe = (() => {
    try { return window.self !== window.top; } catch { return true; }
  })();
  const { toast } = useToast();
  const { setRecordingActive } = useRecordingStatus();
  const updateSession = useMutation(api.recordingSessions.update);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioApiRecordingIdRef = useRef<string | null>(null);
  const gatewaySessionIdRef = useRef<string | null>(null);
  const chunkIndexRef = useRef(0);
  const chunkQueueRef = useRef<Blob[]>([]);
  const isUploadingChunkRef = useRef(false);
  const recordingTimeRef = useRef(0);
  const chunkUploadFailureRef = useRef<any>(null);
  const endRequestedRef = useRef(false);
  const interimIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const periodicRequestDataRef = useRef<NodeJS.Timeout | null>(null);
  const maxRecordingMsRef = useRef<number | null>(null);
  const limitReachedRef = useRef(false);
  const stopRecordingRef = useRef<(() => Promise<void>) | null>(null);

  const { data: siteConfigData } = useRQQuery<{ maxRecordingMs: number }>({
    queryKey: ["/api/admin/config"],
  });
  maxRecordingMsRef.current = siteConfigData?.maxRecordingMs ?? null;

  useEffect(() => {
    setRecordingActive(isRecording || isUploading);
  }, [isRecording, isUploading, setRecordingActive]);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        recordingTimeRef.current++;
        setRecordingTime(prev => prev + 1);
        const maxMs = maxRecordingMsRef.current;
        if (
          maxMs !== null &&
          recordingTimeRef.current * 1000 >= maxMs &&
          !limitReachedRef.current
        ) {
          limitReachedRef.current = true;
          toast({
            title: "Maximum recording time reached",
            description:
              "Your recording has been automatically stopped and will be processed.",
          });
          stopRecordingRef.current?.();
        }
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRecording || isUploading || isTranscribing) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      setRecordingActive(false);
      if (interimIntervalRef.current) {
        clearInterval(interimIntervalRef.current);
        interimIntervalRef.current = null;
      }
      if (periodicRequestDataRef.current) {
        clearInterval(periodicRequestDataRef.current);
        periodicRequestDataRef.current = null;
      }
    };
  }, [isRecording, isUploading, isTranscribing, setRecordingActive]);

  const describeChunkUploadError = (err: any): { title: string; description: string } => {
    const stage: string | undefined = err?.stage;
    const httpStatus: number | undefined = err?.status;
    if (stage === "auth-gateway-network") {
      return { title: "Auth gateway unreachable", description: "Couldn't reach the auth gateway. Check your network connection." };
    }
    if (stage === "auth-gateway-http") {
      return {
        title: httpStatus === 401 ? "Recording interrupted" : (httpStatus ? `Auth gateway error (${httpStatus})` : "Auth gateway error"),
        description: httpStatus === 401
          ? "Your audio is already saved to the cloud. Reload the page to reconnect and recover the recording."
          : "The auth gateway rejected the upload.",
      };
    }
    if (stage === "id-token-expired") {
      return {
        title: "Recording interrupted",
        description: "Your session token could not be refreshed. Your audio is already saved to the cloud — reload the page to reconnect and recover.",
      };
    }
    if (stage === "audio-api-network") {
      return { title: "Upload failed", description: "Couldn't reach the audio service. Check your network connection." };
    }
    if (stage === "audio-api-http") {
      if (httpStatus === 401) {
        return {
          title: "Recording interrupted",
          description: "Your audio is already saved to the cloud. Reload the page to reconnect and recover the recording.",
        };
      }
      return {
        title: httpStatus ? `Audio service error (${httpStatus})` : "Upload failed",
        description: "The audio service rejected the upload. Please try again.",
      };
    }
    return { title: "Upload failed", description: "A chunk upload failed after multiple attempts. Please try again." };
  };

  const uploadChunk = async (blob: Blob): Promise<boolean> => {
    const recordingId = audioApiRecordingIdRef.current;
    if (!recordingId) return false;

    try {
      await audioApiUploadChunk(recordingId, chunkIndexRef.current, blob);
      chunkIndexRef.current++;
      return true;
    } catch (err: any) {
      console.error("Chunk upload failed:", err, { stage: err?.stage, status: err?.status });
      chunkUploadFailureRef.current = err;
      return false;
    }
  };

  const uploadCompletePromiseRef = useRef<Promise<void> | null>(null);
  const uploadResolveRef = useRef<(() => void) | null>(null);

  const processChunkQueue = async () => {
    if (isUploadingChunkRef.current) return;
    isUploadingChunkRef.current = true;

    while (chunkQueueRef.current.length > 0) {
      const chunk = chunkQueueRef.current.shift()!;
      const success = await uploadChunk(chunk);
      if (!success) {
        console.error("Failed to upload chunk after retries");
        chunkQueueRef.current = [];

        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
          if (interimIntervalRef.current) {
            clearInterval(interimIntervalRef.current);
            interimIntervalRef.current = null;
          }
          if (periodicRequestDataRef.current) {
            clearInterval(periodicRequestDataRef.current);
            periodicRequestDataRef.current = null;
          }
          setIsRecording(false);
          setIsUploading(false);
          setUploadProgress("");
          const { title, description } = describeChunkUploadError(chunkUploadFailureRef.current);
          toast({ title, description, variant: "destructive" });
        }

        break;
      }
    }

    isUploadingChunkRef.current = false;
    if (uploadResolveRef.current) {
      uploadResolveRef.current();
      uploadResolveRef.current = null;
      uploadCompletePromiseRef.current = null;
    }
  };

  const waitForAllUploads = (): Promise<void> => {
    if (!isUploadingChunkRef.current && chunkQueueRef.current.length === 0) {
      return Promise.resolve();
    }
    if (!uploadCompletePromiseRef.current) {
      uploadCompletePromiseRef.current = new Promise<void>((resolve) => {
        uploadResolveRef.current = resolve;
      });
    }
    return uploadCompletePromiseRef.current;
  };


  const getSupportedMimeType = (): string | null => {
    const isSafari =
      typeof navigator !== "undefined" &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const candidates = isSafari
      ? ["audio/mp4", "audio/webm", "audio/ogg", "audio/wav"]
      : ["audio/webm", "audio/mp4", "audio/ogg", "audio/wav"];
    for (const mimeType of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return null;
  };

  const activeMimeTypeRef = useRef<string>("audio/webm");

  const startRecording = async () => {
    limitReachedRef.current = false;
    setIsPreparing(true);
    let failStage = "init";
    let detectedMimeType: string | null = null;

    const reportError = (stage: string, error: unknown) => {
      try {
        const errName = error instanceof Error ? error.name : "UnknownError";
        const errMsg = error instanceof Error ? error.message : String(error);
        fetch("/api/client-errors/recording", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            stage,
            errorName: errName,
            errorMessage: errMsg.slice(0, 512),
            userAgent: navigator.userAgent.slice(0, 512),
            mimeType: detectedMimeType || activeMimeTypeRef.current || "",
            sessionId: String(sessionId),
            context: "startRecording",
          }),
        }).catch(() => {});
      } catch {}
    };

    try {
      if (isInIframe) {
        setIsPreparing(false);
        toast({
          title: "Open in a new tab to record",
          description: "Microphone access is blocked inside embedded previews. Please open the app in a new browser tab to use audio recording.",
          variant: "destructive",
        });
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setIsPreparing(false);
        toast({
          title: "Recording not supported",
          description: "Recording is not supported in this browser or context. Please use a modern browser with a secure (HTTPS) connection.",
          variant: "destructive",
        });
        return;
      }

      detectedMimeType = getSupportedMimeType();
      if (!detectedMimeType) {
        setIsPreparing(false);
        toast({
          title: "Recording not supported",
          description: "Your browser does not support any compatible audio recording format.",
          variant: "destructive",
        });
        return;
      }
      activeMimeTypeRef.current = detectedMimeType;

      failStage = "getUserMedia";
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      chunkIndexRef.current = 0;
      chunkQueueRef.current = [];
      chunkUploadFailureRef.current = null;

      // Create a gateway session first to get a server-minted UUID
      failStage = "audioApiCreateSession";
      let gatewaySessionUUID: string;
      try {
        const sessionData = await audioApiCreateSession("Recording session");
        gatewaySessionUUID = sessionData.id;
      } catch (err: any) {
        stream.getTracks().forEach(track => track.stop());
        if (err?.stage) {
          failStage = err.stage;
          throw err;
        }
        throw err;
      }
      gatewaySessionIdRef.current = gatewaySessionUUID;

      await updateSession({
        id: sessionId,
        userId,
        gatewaySessionId: gatewaySessionUUID,
        audioMimeType: detectedMimeType,
        status: "recording",
      });

      failStage = "audioApiStart";
      let startData: { id?: string; recordingId?: string } = {};
      try {
        startData = await audioApiStart(gatewaySessionUUID, detectedMimeType);
      } catch (err: any) {
        stream.getTracks().forEach(track => track.stop());
        if (err?.status === 401 || (err instanceof Error && err.message.includes("401"))) {
          throw new Error("401 Unauthorized");
        }
        if (err?.stage) {
          failStage = err.stage;
          throw err;
        }
        throw err;
      }

      const actualRecordingId = startData.recordingId || startData.id || "";
      audioApiRecordingIdRef.current = actualRecordingId;

      await updateSession({
        id: sessionId,
        userId,
        audioApiRecordingId: actualRecordingId,
      });

      failStage = "MediaRecorder";
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType: detectedMimeType });
      } catch {
        recorder = new MediaRecorder(stream);
      }
      const actualMimeType = recorder.mimeType || detectedMimeType;
      activeMimeTypeRef.current = actualMimeType;
      if (actualMimeType !== detectedMimeType) {
        updateSession({ id: sessionId, userId, audioMimeType: actualMimeType }).catch(() => {});
      }

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunkQueueRef.current.push(e.data);
          processChunkQueue();
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
      };

      failStage = "recorder.start";
      try {
        recorder.start(5000);
      } catch {
        recorder.start();
        periodicRequestDataRef.current = setInterval(() => {
          if (recorder.state === "recording") {
            recorder.requestData();
          }
        }, 5000);
      }

      setMediaRecorder(recorder);
      mediaRecorderRef.current = recorder;
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      setIsPreparing(false);
      setIsRecording(true);

      toast({ title: "Recording started", description: "Speak clearly into your microphone." });
    } catch (error: unknown) {
      setIsPreparing(false);
      console.error("Failed to start recording:", error);
      const errorStage: string = (error as any)?.stage || failStage;
      reportError(errorStage, error);

      let title = "Recording failed";
      let description = "An unexpected error occurred. Please try again.";

      const errMsg = error instanceof Error ? error.message : String(error);
      const errName = error instanceof Error ? error.name : "";
      const httpStatus: number | undefined = (error as any)?.status;

      if (errMsg.includes("401") || errMsg.includes("Unauthorized")) {
        title = "Not logged in";
        description = "Please log in to record audio.";
      } else if (errorStage === "firebase-token") {
        title = "Sign-in expired";
        description = "Please sign in again to record.";
      } else if (errorStage === "auth-gateway-config") {
        title = "Auth gateway not configured";
        description = "The auth gateway URL is missing. Please contact support.";
      } else if (errorStage === "auth-gateway-network") {
        title = "Auth gateway unreachable";
        description = "Couldn't reach the auth gateway from this browser. This is often a CORS or network issue.";
      } else if (errorStage === "auth-gateway-http") {
        title = httpStatus ? `Auth gateway error (${httpStatus})` : "Auth gateway error";
        description = "The auth gateway rejected the request.";
      } else if (errorStage === "audio-api-network") {
        title = "Audio service unreachable";
        description = "Couldn't reach the audio service.";
      } else if (errorStage === "audio-api-http") {
        title = httpStatus ? `Audio service error (${httpStatus})` : "Audio service error";
        description = "The audio service rejected the request.";
      } else if (
        errName === "NotAllowedError" ||
        errName === "NotFoundError" ||
        errMsg.includes("Permission denied") ||
        errMsg.includes("Requested device not found")
      ) {
        title = "Microphone access denied";
        description = "Please allow microphone access to record audio.";
      } else if (errName === "NotSupportedError") {
        title = "Recording not supported";
        description = "Your browser does not support the required audio format for recording.";
      } else if (errName) {
        description = `An unexpected error occurred (${errName}). Please try again.`;
      }

      toast({ title, description, variant: "destructive" });
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorder || mediaRecorder.state === "inactive") return;

    if (interimIntervalRef.current) {
      clearInterval(interimIntervalRef.current);
      interimIntervalRef.current = null;
    }

    if (periodicRequestDataRef.current) {
      clearInterval(periodicRequestDataRef.current);
      periodicRequestDataRef.current = null;
    }

    mediaRecorder.stop();
    setIsRecording(false);

    setIsUploading(true);
    setUploadProgress("Finishing upload...");

    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (chunkQueueRef.current.length > 0 || isUploadingChunkRef.current) {
        processChunkQueue();
      }
      await waitForAllUploads();

      if (chunkUploadFailureRef.current) {
        const { title, description } = describeChunkUploadError(chunkUploadFailureRef.current);
        setIsUploading(false);
        setUploadProgress("");
        toast({ title, description, variant: "destructive" });
        return;
      }

      const totalChunks = chunkIndexRef.current;
      if (totalChunks === 0) {
        setIsUploading(false);
        toast({ title: "No audio recorded", description: "The recording was too short.", variant: "destructive" });
        return;
      }

      try {
        await updateSession({
          id: sessionId,
          userId,
          status: "processing",
        });
      } catch {}

      const recordingId = audioApiRecordingIdRef.current!;
      setUploadProgress("Assembling audio...");

      const completeData = await audioApiComplete(recordingId, totalChunks);
      const audioUrl = completeData.audioUrl || completeData.url || completeData.s3Url;

      await updateSession({
        id: sessionId,
        userId,
        audioUrl,
        duration: recordingTimeRef.current,
      });

      setIsUploading(false);
      setUploadProgress("");
      setShowStopModal(true);

      const gwSessionId = gatewaySessionIdRef.current || "";
      pollGatewayTranscript(recordingId, gwSessionId).catch((error) => {
        console.error("Background transcription error:", error);
      });
    } catch (error) {
      console.error("Stop recording error:", error);
      const isSessionExpired = error instanceof Error && error.message === "Session expired";
      const hasResumeData = audioApiRecordingIdRef.current && chunkIndexRef.current > 0;
      if (!isSessionExpired) {
        try {
          await updateSession({
            id: sessionId,
            userId,
            status: "failed",
            duration: recordingTimeRef.current,
          });
        } catch {}
      }
      toast({
        title: isSessionExpired || hasResumeData ? "Session interrupted" : "Recording failed",
        description: isSessionExpired || hasResumeData
          ? "Your audio was saved to the cloud. It will be automatically processed when you log in again."
          : "There was an error processing your recording. Please try again.",
        variant: "destructive",
      });
      setIsUploading(false);
      setUploadProgress("");
      setRecordingTime(0);
    }
  };

  stopRecordingRef.current = stopRecording;

  const pollGatewayTranscript = async (
    audioApiRecordingId: string,
    gatewaySessionId: string,
  ) => {
    const localSessionId = sessionId;
    const localUserId = userId;
    const localExistingTranscription = existingTranscription;
    const localDuration = recordingTimeRef.current;

    setIsTranscribing(true);

    try {
      try {
        await updateSession({ id: localSessionId, userId: localUserId, status: "processing" });
      } catch {}

      let transcript: string | null = null;
      const maxAttempts = 120;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise<void>((r) => setTimeout(r, 5000));

        let statusData: any;
        try {
          statusData = await audioApiGetStatus(audioApiRecordingId);
        } catch (err) {
          console.warn("[pollGatewayTranscript] Status fetch failed:", err);
          continue;
        }

        const recStatus: string = statusData?.status ?? "";

        if (recStatus === "transcription_failed" || recStatus === "failed") {
          throw new Error("transcription_failed");
        }

        if (recStatus === "transcription_complete" || recStatus === "completed") {
          try {
            const transcriptData = await audioApiGetSessionTranscript(gatewaySessionId);
            if (transcriptData !== null && transcriptData?.transcript) {
              transcript = transcriptData.transcript;
              break;
            }
          } catch (err) {
            console.warn("[pollGatewayTranscript] Transcript fetch failed:", err);
          }
        }
      }

      if (!transcript) {
        throw new Error("Transcription timed out");
      }

      const finalTranscription = localExistingTranscription
        ? `${localExistingTranscription}\n\n${transcript}`
        : transcript;

      await updateSession({
        id: localSessionId,
        userId: localUserId,
        transcription: finalTranscription,
        status: "completed",
        duration: localDuration,
      });

      toast({ title: "Transcription complete", description: "Your audio has been transcribed." });
      onTranscriptionComplete();

      if (endRequestedRef.current && onEndSession) {
        onEndSession();
        endRequestedRef.current = false;
      }
    } catch (error) {
      console.error("Gateway transcription error:", error);
      const isFailed = error instanceof Error && error.message === "transcription_failed";
      try {
        await updateSession({
          id: localSessionId,
          userId: localUserId,
          status: "failed",
          duration: localDuration,
        });
      } catch {}
      toast({
        title: "Transcription failed",
        description: isFailed
          ? "The transcription service could not process your audio. Your recording is saved — you can retry later."
          : "Your audio was saved to the cloud. You can retry transcription later.",
        variant: "destructive",
      });
      endRequestedRef.current = false;
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isProcessingUpload = isUploading;

  if ((isProcessingUpload || (sessionEnded && (isTranscribing || isUploading))) && !showStopModal) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="transcription-progress">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          Processing Recording...
        </h3>
        <p className="text-muted-foreground max-w-sm">
          {uploadProgress || "Please wait while we process your recording."}
        </p>
      </div>
    );
  }

  if (sessionEnded && !showStopModal) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="transcription-progress">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          Transcribing Recording...
        </h3>
        <p className="text-muted-foreground max-w-sm">
          Please wait while we transcribe your recording.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <button
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isPreparing}
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-all ${
          isRecording 
            ? "bg-destructive hover:bg-destructive/90 animate-pulse" 
            : isPreparing
              ? "bg-primary/70 cursor-not-allowed"
              : "bg-primary hover:bg-primary/90"
        }`}
        data-testid="button-record"
      >
        {isRecording ? (
          <Square className="w-8 h-8 text-destructive-foreground" />
        ) : isPreparing ? (
          <Loader2 className="w-8 h-8 text-primary-foreground animate-spin" />
        ) : (
          <Mic className="w-8 h-8 text-primary-foreground" />
        )}
      </button>
      
      {isRecording && (
        <p className="text-2xl font-mono text-foreground mb-2" data-testid="text-recording-time">
          {formatTime(recordingTime)}
        </p>
      )}
      
      <h3 className="text-lg font-medium text-foreground mb-2">
        {isRecording ? "Recording..." : isPreparing ? "Starting…" : "Record Audio"}
      </h3>
      <p className="text-muted-foreground max-w-sm">
        {isRecording 
          ? "Click the stop button when you're done recording."
          : isPreparing
            ? "Please wait while we prepare your recording."
            : "Click the microphone button to start recording your consultation."}
      </p>
      {isInIframe && !isRecording && (
        <p className="text-sm text-muted-foreground mt-3">
          Microphone requires a direct browser tab.{" "}
          <a
            href={window.location.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:no-underline"
            data-testid="link-open-new-tab"
          >
            Open in new tab
          </a>
        </p>
      )}

      <Dialog open={showStopModal} onOpenChange={setShowStopModal}>
        <DialogContent className="sm:max-w-md" data-testid="modal-stop-recording">
          <DialogHeader>
            <DialogTitle>Recording Stopped</DialogTitle>
            <DialogDescription>
              Your audio is being transcribed in the background. What would you like to do?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setShowStopModal(false)}
              data-testid="button-pause-session"
            >
              Pause
            </Button>
            <Button
              onClick={() => {
                endRequestedRef.current = true;
                setSessionEnded(true);
                setShowStopModal(false);
                if (!isTranscribing && onEndSession) {
                  onEndSession();
                  endRequestedRef.current = false;
                }
              }}
              data-testid="button-end-session"
            >
              End Recording
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RetryTranscription({ 
  sessionId, 
  userId,
  audioApiRecordingId,
  gatewaySessionId,
  existingTranscription,
  onTranscriptionComplete 
}: { 
  sessionId: Id<"recordingSessions">; 
  userId: Id<"users">;
  audioApiRecordingId?: string;
  gatewaySessionId?: string;
  existingTranscription?: string;
  onTranscriptionComplete: () => void;
}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const { toast } = useToast();
  const updateSession = useMutation(api.recordingSessions.update);

  const retryTranscription = async () => {
    setIsRetrying(true);
    
    try {
      if (!audioApiRecordingId) {
        throw new Error("No recording ID available for retry");
      }

      if (!gatewaySessionId) {
        throw new Error("legacy-no-gateway-id");
      }

      try {
        await updateSession({ id: sessionId, userId, status: "processing" });
      } catch {}

      // Check current recording status to decide whether to re-trigger completion
      let statusData: any;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          statusData = await audioApiGetStatus(audioApiRecordingId);
          break;
        } catch (err) {
          console.warn(`[RetryTranscription] Status fetch attempt ${attempt + 1} failed:`, err);
          if (attempt < 2) await new Promise<void>((r) => setTimeout(r, 2000));
        }
      }
      if (!statusData) {
        throw new Error("Could not reach the recording service to check status");
      }

      const recStatus: string = statusData?.status ?? "";
      const alreadyComplete = recStatus === "transcription_complete" || recStatus === "completed";

      if (!alreadyComplete) {
        // Re-trigger completion so the gateway re-runs transcription
        const totalChunks = statusData?.totalChunks || statusData?.chunksReceived || 0;
        if (totalChunks === 0) {
          throw new Error("No audio chunks found for this recording");
        }
        await audioApiComplete(audioApiRecordingId, totalChunks);
      }

      // Poll recording status until transcription complete or failed
      let transcript: string | null = null;
      const maxAttempts = 60;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise<void>((r) => setTimeout(r, 5000));

        let latestStatus: any;
        try {
          latestStatus = await audioApiGetStatus(audioApiRecordingId);
        } catch { continue; }

        const latestRecStatus: string = latestStatus?.status ?? "";

        if (latestRecStatus === "transcription_failed" || latestRecStatus === "failed") {
          throw new Error("transcription_failed");
        }

        if (latestRecStatus === "transcription_complete" || latestRecStatus === "completed") {
          try {
            const transcriptData = await audioApiGetSessionTranscript(gatewaySessionId);
            if (transcriptData !== null && transcriptData?.transcript) {
              transcript = transcriptData.transcript;
              break;
            }
          } catch { continue; }
        }
      }

      if (!transcript) {
        throw new Error("Transcription timed out");
      }

      const finalTranscription = existingTranscription 
        ? `${existingTranscription}\n\n${transcript}`
        : transcript;

      await updateSession({
        id: sessionId,
        userId,
        transcription: finalTranscription,
        status: "completed",
        clearAudioData: true,
      });

      toast({ title: "Transcription complete", description: "Your audio has been transcribed." });
      onTranscriptionComplete();
    } catch (error) {
      console.error("Retry transcription error:", error);
      const isFailed = error instanceof Error && error.message === "transcription_failed";
      const isLegacy = error instanceof Error && error.message === "legacy-no-gateway-id";
      if (!isLegacy) {
        try {
          await updateSession({ id: sessionId, userId, status: "failed" });
        } catch {}
      }
      toast({ 
        title: isLegacy ? "Cannot retry this recording" : "Transcription failed", 
        description: isLegacy
          ? "This recording was created before the current transcription system. Please record new audio."
          : isFailed
          ? "The transcription service could not process your audio. Please try again later."
          : "Could not transcribe your audio. Please try again later.",
        variant: "destructive" 
      });
    } finally {
      setIsRetrying(false);
    }
  };

  if (isRetrying) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
        <h3 className="text-base font-medium text-foreground mb-1">
          Retrying Transcription...
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Please wait while we convert your recording to text.
        </p>
      </div>
    );
  }

  return (
    <Button 
      onClick={retryTranscription}
      className="mt-4 gap-2"
      data-testid="button-retry-transcription"
    >
      <Sparkles className="w-4 h-4" />
      Retry Transcription
    </Button>
  );
}

function NewSessionDialog({ userId, onCreated, disabled }: { userId: Id<"users">; onCreated: (id: Id<"recordingSessions">) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [gender, setGender] = useState<string>("none");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const { toast } = useToast();
  
  const templates = useQuery(api.templates.list, { userId });
  const createSession = useMutation(api.recordingSessions.create);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newId = await createSession({
        userId,
        patientName,
        gender: gender === "male" || gender === "female" ? gender : undefined,
        title: title.trim() || `Session - ${format(new Date(), "MMM d, yyyy")}`,
        recordingDate: Date.now(),
        notes: notes || undefined,
        templateId: selectedTemplateId && selectedTemplateId !== "none" ? selectedTemplateId as Id<"templates"> : undefined,
      });
      toast({ title: "Session created", description: "Your recording session has been created." });
      setOpen(false);
      setPatientName("");
      setGender("none");
      setTitle("");
      setNotes("");
      setSelectedTemplateId("");
      onCreated(newId);
    } catch {
      toast({ title: "Error", description: "Failed to create session.", variant: "destructive" });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <DialogTrigger asChild>
        <Button className="w-full gap-2" disabled={disabled} data-testid="button-new-session">
          <Plus className="w-4 h-4" />
          New Session
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Recording Session</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="patientName">Patient Name</Label>
            <Input 
              id="patientName" 
              value={patientName} 
              onChange={(e) => setPatientName(e.target.value)} 
              placeholder="John Doe"
              required
              data-testid="input-patient-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender">Gender (Optional)</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger data-testid="select-gender">
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="option-gender-none">Not specified</SelectItem>
                <SelectItem value="male" data-testid="option-gender-male">Male</SelectItem>
                <SelectItem value="female" data-testid="option-gender-female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Session Title (Optional)</Label>
            <Input 
              id="title" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Initial Consultation"
              data-testid="input-session-title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="template">Template (Optional)</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger data-testid="select-template">
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates?.map((template) => (
                  <SelectItem key={template._id} value={template._id} data-testid={`option-template-${template._id}`}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea 
              id="notes" 
              value={notes} 
              onChange={(e) => setNotes(e.target.value)} 
              placeholder="Any additional notes..."
              data-testid="input-session-notes"
            />
          </div>
          <Button type="submit" className="w-full" data-testid="button-create-session">
            Create Session
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditSessionDialog({ 
  session, 
  userId, 
  onSuccess 
}: { 
  session: RecordingSession; 
  userId: Id<"users">; 
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [patientName, setPatientName] = useState(session.patientName);
  const [gender, setGender] = useState<string>(session.gender || "none");
  const [title, setTitle] = useState(session.title);
  const [notes, setNotes] = useState(session.notes || "");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(session.templateId || "none");
  const { toast } = useToast();
  
  const templates = useQuery(api.templates.list, { userId });
  const updateSession = useMutation(api.recordingSessions.update);

  useEffect(() => {
    if (open) {
      setPatientName(session.patientName);
      setGender(session.gender || "none");
      setTitle(session.title);
      setNotes(session.notes || "");
      setSelectedTemplateId(session.templateId || "none");
    }
  }, [open, session]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const templateIdToSet = selectedTemplateId && selectedTemplateId !== "none" 
        ? selectedTemplateId as Id<"templates"> 
        : undefined;
      
      await updateSession({
        id: session._id,
        userId,
        patientName,
        gender: gender === "male" || gender === "female" ? gender : undefined,
        clearGender: gender !== "male" && gender !== "female",
        title,
        notes: notes || undefined,
        templateId: templateIdToSet,
        clearTemplateId: selectedTemplateId === "none" || selectedTemplateId === "",
      });
      toast({ title: "Session updated", description: "Your recording session has been updated." });
      setOpen(false);
      onSuccess();
    } catch {
      toast({ title: "Error", description: "Failed to update session.", variant: "destructive" });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="button-edit-session">
          <Pencil className="w-4 h-4" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Recording Session</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="editPatientName">Patient Name</Label>
            <Input 
              id="editPatientName" 
              value={patientName} 
              onChange={(e) => setPatientName(e.target.value)} 
              placeholder="John Doe"
              required
              data-testid="input-edit-patient-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="editGender">Gender (Optional)</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger data-testid="select-edit-gender">
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="option-edit-gender-none">Not specified</SelectItem>
                <SelectItem value="male" data-testid="option-edit-gender-male">Male</SelectItem>
                <SelectItem value="female" data-testid="option-edit-gender-female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="editTitle">Session Title</Label>
            <Input 
              id="editTitle" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Initial Consultation"
              required
              data-testid="input-edit-session-title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="editTemplate">Template (Optional)</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger data-testid="select-edit-template">
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates?.map((template) => (
                  <SelectItem key={template._id} value={template._id} data-testid={`option-edit-template-${template._id}`}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="editNotes">Notes (Optional)</Label>
            <Textarea 
              id="editNotes" 
              value={notes} 
              onChange={(e) => setNotes(e.target.value)} 
              placeholder="Any additional notes..."
              data-testid="input-edit-session-notes"
            />
          </div>
          <Button type="submit" className="w-full" data-testid="button-save-session">
            Save Changes
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NotesEditor({ 
  sessionId, 
  userId, 
  notes,
  onSaved,
  speakerAttributionStatus,
  hasAttributionResult,
  onViewSpeakers,
  onRetryAttribution,
  onLabelSpeakers,
  isAttributing,
}: { 
  sessionId: Id<"recordingSessions">; 
  userId: Id<"users">;
  notes?: string;
  onSaved?: () => void;
  speakerAttributionStatus?: "processing" | "done" | "failed";
  hasAttributionResult?: boolean;
  onViewSpeakers?: () => void;
  onRetryAttribution?: () => void;
  onLabelSpeakers?: () => void;
  isAttributing?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [notesValue, setNotesValue] = useState(notes || "");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const updateSession = useMutation(api.recordingSessions.update);

  useEffect(() => {
    setNotesValue(notes || "");
  }, [notes, sessionId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSession({
        id: sessionId,
        userId,
        notes: notesValue || undefined,
      });
      toast({ title: "Notes saved", description: "Your notes have been updated." });
      setIsEditing(false);
      onSaved?.();
    } catch {
      toast({ 
        title: "Error", 
        description: "Failed to save notes. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotesValue(notes || "");
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-foreground">Session Notes</h3>
          {!isEditing && notes && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(notes);
                  toast({ title: "Notes copied to clipboard" });
                } catch {
                  toast({
                    title: "Error",
                    description: "Failed to copy notes.",
                    variant: "destructive",
                  });
                }
              }}
              title="Copy notes"
              data-testid="button-copy-notes"
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
        {!isEditing && (
          <div className="flex items-center gap-1">
            {onLabelSpeakers && notes?.trim() && !speakerAttributionStatus && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={onLabelSpeakers}
                disabled={isAttributing}
                data-testid="button-label-speakers-notes"
              >
                {isAttributing ? (
                  <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Labeling…</>
                ) : (
                  <><Users className="w-3 h-3 mr-1.5" />Label Speakers</>
                )}
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditing(true)}
              data-testid="button-edit-notes"
            >
              <Pencil className="w-3 h-3 mr-1" />
              {notes ? "Edit" : "Add Notes"}
            </Button>
          </div>
        )}
      </div>
      
      {isEditing ? (
        <div className="flex flex-col flex-1 min-h-0 gap-3">
          <Textarea
            value={notesValue}
            onChange={(e) => setNotesValue(e.target.value)}
            placeholder="Add notes about this session (behavior, location, duration, observations, etc.)..."
            className="flex-1 min-h-0 resize-none"
            data-testid="input-notes"
          />
          <div className="flex gap-2 flex-shrink-0">
            <Button 
              size="sm" 
              onClick={handleSave} 
              disabled={isSaving}
              data-testid="button-save-notes"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleCancel}
              disabled={isSaving}
              data-testid="button-cancel-notes"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : notes ? (
        <div className="flex-1 min-h-0 overflow-auto">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="text-session-notes">
            {notes}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic flex-shrink-0">
          No notes added yet. Click "Add Notes" to add observations, behaviors, or other details.
        </p>
      )}

      {speakerAttributionStatus && (
        <div className="mt-4 pt-4 border-t border-border/50">
          {speakerAttributionStatus === "processing" && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid="status-speaker-labeling-processing"
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              Updating speaker labels…
            </span>
          )}
          {speakerAttributionStatus === "done" && hasAttributionResult && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid="status-speaker-labeling-done"
            >
              <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400" />
              Speaker labels updated
              {onViewSpeakers && (
                <button
                  className="underline text-foreground ml-1 hover:text-primary transition-colors"
                  onClick={onViewSpeakers}
                  data-testid="link-view-speakers"
                >
                  View speakers
                </button>
              )}
            </span>
          )}
          {speakerAttributionStatus === "failed" && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-destructive"
              data-testid="status-speaker-labeling-failed"
            >
              <AlertCircle className="w-3 h-3" />
              Speaker labeling failed
              {onRetryAttribution && (
                <button
                  className="underline ml-1 hover:opacity-80 transition-opacity"
                  onClick={onRetryAttribution}
                  data-testid="button-retry-speaker-labeling"
                >
                  Retry
                </button>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function markdownToPlainText(md: string): string {
  let text = md;
  text = text.replace(/```[^\n]*\n([\s\S]*?)```/g, (_m, code) => code.replace(/\n$/, ""));
  text = text.replace(/~~~[^\n]*\n([\s\S]*?)~~~/g, (_m, code) => code.replace(/\n$/, ""));
  text = text.replace(/`([^`]+)`/g, "$1");
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  text = text.replace(/^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/gm, "$1");
  text = text.replace(/^\s{0,3}(?:[-*_]\s*){3,}\s*$/gm, "");
  text = text.replace(/^\s{0,3}>\s?/gm, "");
  text = text.replace(/^(\s*)[-*+]\s+/gm, "$1- ");
  text = text.replace(/^(\s*)\d+\.\s+/gm, "$1");
  text = text.replace(/\*\*([^*]+)\*\*/g, "$1");
  text = text.replace(/__([^_]+)__/g, "$1");
  text = text.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1$2");
  text = text.replace(/(^|[^_\w])_([^_\n]+)_/g, "$1$2");
  text = text.replace(/~~([^~]+)~~/g, "$1");
  text = text.replace(/\n{3,}/g, "\n\n");
  return text.trim();
}

function extractPlaceholders(template: string): string[] {
  const matches = template.match(/\{\{[a-zA-Z0-9_.-]+\}\}/g);
  if (!matches) return [];
  return Array.from(new Set(matches.map((m: string) => m.replace(/\{\{|\}\}/g, ''))));
}

function extractValuesFromRendered(formatTemplate: string, renderedText: string): { values: Record<string, string>; matched: boolean } {
  const allOccurrences: string[] = [];
  const occurrenceRegex = /\{\{([a-zA-Z0-9_.-]+)\}\}/g;
  let m;
  while ((m = occurrenceRegex.exec(formatTemplate)) !== null) {
    allOccurrences.push(m[1]);
  }
  if (allOccurrences.length === 0) return { values: {}, matched: true };

  const escapedParts = formatTemplate.split(/\{\{[a-zA-Z0-9_.-]+\}\}/g).map(
    part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  let regexStr = '';
  for (let i = 0; i < escapedParts.length; i++) {
    regexStr += escapedParts[i];
    if (i < allOccurrences.length) {
      regexStr += '([\\s\\S]*?)';
    }
  }

  const regex = new RegExp('^' + regexStr + '$', 's');
  const match = renderedText.match(regex);
  const values: Record<string, string> = {};

  if (match) {
    allOccurrences.forEach((key, idx) => {
      values[key] = match[idx + 1] ?? '';
    });
    return { values, matched: true };
  }

  return { values: {}, matched: false };
}

function mergeTemplateWithValues(template: string, values: Record<string, string>): string {
  let result = template;
  const placeholders = extractPlaceholders(template);
  for (const key of placeholders) {
    const val = (values[key] !== undefined && values[key] !== null)
      ? String(values[key])
      : "Not documented";
    result = result.split(`{{${key}}}`).join(val);
  }
  return result;
}

type TemplatePart = { type: 'text'; text: string } | { type: 'placeholder'; key: string; value: string };

function parseTemplateIntoParts(formatTemplate: string, values: Record<string, string>): TemplatePart[] {
  const parts: TemplatePart[] = [];
  const regex = /\{\{([a-zA-Z0-9_.-]+)\}\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(formatTemplate)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', text: formatTemplate.slice(lastIndex, match.index) });
    }
    const key = match[1];
    parts.push({ type: 'placeholder', key, value: values[key] ?? '' });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < formatTemplate.length) {
    parts.push({ type: 'text', text: formatTemplate.slice(lastIndex) });
  }

  return parts;
}

function RegeneratingPlaceholder({ testId, stage }: { testId?: string; stage?: ProgressStage }) {
  const widths = ["92%", "85%", "96%", "78%", "64%"];
  return (
    <div className="relative my-3">
      <div
        className="space-y-2"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Regenerating section"
        data-testid={testId}
      >
        <span className="sr-only">Regenerating section…</span>
        {widths.map((w, i) => (
          <div
            key={i}
            className="h-4 rounded bg-muted animate-pulse"
            style={{ width: w, animationDelay: `${i * 120}ms` }}
          />
        ))}
      </div>
      {stage && stage !== "idle" && stage !== "done" && (
        <SectionGenerationProgress
          stage={stage}
          testId={testId ? `${testId}-progress` : undefined}
        />
      )}
    </div>
  );
}

function EditableStaticSection({
  formatTemplate,
  renderedContent,
  onContentChange,
  sectionTitle,
  sectionId,
  sectionRef,
  onRegenerate,
  onCopy,
  onLearn,
  onAssist,
  onHistory,
  showHistoryButton,
  regeneratingSectionId,
  copiedSectionId,
  regenerationStage,
  viewMode,
  onToggleViewMode,
}: {
  formatTemplate: string;
  renderedContent: string;
  onContentChange: (newContent: string) => void;
  sectionTitle: string;
  sectionId: string;
  sectionRef: (el: HTMLElement | null) => void;
  onRegenerate: (title: string, id: string) => void;
  onCopy: (id: string, mode: "markdown" | "plain") => void;
  onFocus: (title: string, id: string) => void;
  onLearn: (title: string, id: string) => void;
  onAssist: (title: string, id: string) => void;
  onHistory: (title: string, id: string) => void;
  showHistoryButton: boolean;
  regeneratingSectionId: string | null;
  copiedSectionId: string | null;
  regenerationStage?: ProgressStage;
  viewMode: "markdown" | "plain";
  onToggleViewMode: () => void;
}) {
  const extraction = useMemo(() => extractValuesFromRendered(formatTemplate, renderedContent), [formatTemplate, renderedContent]);
  const [editValues, setEditValues] = useState<Record<string, string>>(extraction.values);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  useEffect(() => {
    setEditValues(extraction.values);
  }, [extraction.values]);

  const handleValueChange = (key: string, newValue: string) => {
    setEditValues(prev => ({ ...prev, [key]: newValue }));
  };

  const handleSave = (_key: string) => {
    setEditingKey(null);
    const newContent = mergeTemplateWithValues(formatTemplate, editValues);
    if (newContent !== renderedContent) {
      onContentChange(newContent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, key: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(key);
    } else if (e.key === 'Escape') {
      setEditValues(prev => ({ ...prev, [key]: extraction.values[key] ?? '' }));
      setEditingKey(null);
    }
  };

  const parts = useMemo(() => parseTemplateIntoParts(formatTemplate, editValues), [formatTemplate, editValues]);

  const PLACEHOLDER_MARKER = '\u200B\u200B';

  const markedContent = useMemo(() => {
    let result = '';
    for (const part of parts) {
      if (part.type === 'text') {
        result += part.text;
      } else {
        result += `${PLACEHOLDER_MARKER}{{${part.key}}}${PLACEHOLDER_MARKER}`;
      }
    }
    return result;
  }, [parts]);

  const renderPlaceholderInput = (key: string) => {
    const isEditing = editingKey === key;
    return isEditing ? (
      <input
        type="text"
        value={editValues[key] ?? ''}
        onChange={(e) => handleValueChange(key, e.target.value)}
        onBlur={() => handleSave(key)}
        onKeyDown={(e) => handleKeyDown(e, key)}
        autoFocus
        className="inline-block border-b-2 border-primary bg-primary/5 px-1 py-0.5 text-sm font-medium text-foreground outline-none min-w-[60px] rounded-sm"
        style={{ width: `${Math.max(60, (editValues[key]?.length || 5) * 8 + 16)}px` }}
        data-testid={`input-placeholder-${key}`}
      />
    ) : (
      <button
        onClick={() => setEditingKey(key)}
        className="inline-block border-b border-dashed border-primary/40 bg-primary/5 px-1 py-0.5 text-sm font-medium text-foreground hover:bg-primary/10 hover:border-primary cursor-text rounded-sm transition-colors"
        title={`Edit ${key.replace(/[_.-]/g, ' ')}`}
        data-testid={`button-edit-placeholder-${key}`}
      >
        {editValues[key] || <span className="italic text-muted-foreground">empty</span>}
      </button>
    );
  };

  const renderTextWithPlaceholders = (text: string) => {
    if (!text.includes(PLACEHOLDER_MARKER)) return <>{text}</>;
    const segments = text.split(PLACEHOLDER_MARKER);
    return (
      <>
        {segments.map((segment, idx) => {
          const placeholderMatch = segment.match(/^\{\{([a-zA-Z0-9_.-]+)\}\}$/);
          if (placeholderMatch) {
            return <span key={idx}>{renderPlaceholderInput(placeholderMatch[1])}</span>;
          }
          return segment ? <span key={idx}>{segment}</span> : null;
        })}
      </>
    );
  };

  const sectionHeader = (
    <h2
      id={sectionId}
      ref={sectionRef}
      className="flex items-center justify-between group text-lg font-semibold mt-6 mb-3"
    >
      <span>{sectionTitle}</span>
      <span className="flex items-center gap-1 shrink-0 ml-2">
        <button
          onClick={onToggleViewMode}
          className="p-1 rounded hover:bg-muted"
          title={viewMode === "plain" ? "Show as markdown" : "Show as plain text"}
          aria-label={viewMode === "plain" ? "Show as markdown" : "Show as plain text"}
          data-testid={`button-toggle-view-${sectionId}`}
        >
          {viewMode === "plain" ? (
            <Code className="w-4 h-4 text-muted-foreground" />
          ) : (
            <FileText className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        <button
          onClick={() => onAssist(sectionTitle, sectionId)}
          className="p-1 rounded hover:bg-muted"
          title="Ask AI about this section"
          data-testid={`button-ai-section-${sectionId}`}
        >
          <Sparkles className="w-4 h-4 text-primary" />
        </button>
        <button
          onClick={() => onLearn(sectionTitle, sectionId)}
          className="p-1 rounded hover:bg-muted"
          title="Teach AI from this section"
          data-testid={`button-learn-section-${sectionId}`}
        >
          <GraduationCap className="w-4 h-4 text-muted-foreground" />
        </button>
        {showHistoryButton && (
          <button
            onClick={() => onHistory(sectionTitle, sectionId)}
            className="p-1 rounded hover:bg-muted"
            title="Section history"
            data-testid={`button-history-section-${sectionId}`}
          >
            <History className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
        <button
          onClick={() => onRegenerate(sectionTitle, sectionId)}
          disabled={regeneratingSectionId !== null}
          className="p-1 rounded hover:bg-muted disabled:opacity-50"
          title="Regenerate section"
          data-testid={`button-regen-section-${sectionId}`}
        >
          {regeneratingSectionId === sectionId ? (
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded hover:bg-muted"
              title="Copy section"
              data-testid={`button-copy-section-${sectionId}`}
            >
              {copiedSectionId === sectionId ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onCopy(sectionId, "plain")}
              data-testid={`button-copy-section-plain-${sectionId}`}
            >
              Copy as plain text
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onCopy(sectionId, "markdown")}
              data-testid={`button-copy-section-markdown-${sectionId}`}
            >
              Copy with markdown
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </h2>
  );

  const stageActive = regenerationStage && regenerationStage !== "done" && regenerationStage !== "error";
  const isRegenerating = regeneratingSectionId === sectionId || !!stageActive;

  if (!extraction.matched) {
    return (
      <div className="mb-6">
        {sectionHeader}
        {isRegenerating ? (
          <RegeneratingPlaceholder
            testId={`placeholder-regenerating-${sectionId}`}
            stage={regenerationStage}
          />
        ) : viewMode === "plain" ? (
          <pre
            className="whitespace-pre-wrap break-words font-sans text-sm text-foreground"
            data-testid={`text-section-plain-${sectionId}`}
          >
            {renderedContent}
          </pre>
        ) : (
          <div className="section-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {renderedContent}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-6">
      {sectionHeader}
      {isRegenerating ? (
        <RegeneratingPlaceholder
          testId={`placeholder-regenerating-${sectionId}`}
          stage={regenerationStage}
        />
      ) : viewMode === "plain" ? (
        <pre
          className="whitespace-pre-wrap break-words font-sans text-sm text-foreground"
          data-testid={`text-section-plain-${sectionId}`}
        >
          {renderedContent}
        </pre>
      ) : (
        <div className="section-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => <p>{processChildren(children)}</p>,
            li: ({ children, ...props }) => <li {...props}>{processChildren(children)}</li>,
            td: ({ children, ...props }) => <td {...props}>{processChildren(children)}</td>,
            th: ({ children, ...props }) => <th {...props}>{processChildren(children)}</th>,
            strong: ({ children }) => <strong>{processChildren(children)}</strong>,
            em: ({ children }) => <em>{processChildren(children)}</em>,
            code: ({ children, ...props }) => <code {...props}>{processChildren(children)}</code>,
          }}
        >
          {markedContent}
        </ReactMarkdown>
        </div>
      )}
    </div>
  );

  function processChildren(children: React.ReactNode): React.ReactNode {
    if (typeof children === 'string') {
      return renderTextWithPlaceholders(children);
    }
    if (Array.isArray(children)) {
      return children.map((child, i) => {
        if (typeof child === 'string') {
          return <span key={i}>{renderTextWithPlaceholders(child)}</span>;
        }
        return child;
      });
    }
    return children;
  }
}

function DocumentGenerator({ 
  transcription,
  notes,
  templateId,
  patientName,
  gender,
  sessionTitle,
  sessionId,
  userId,
  existingDocument,
  existingDocumentGeneratedAt,
  existingModelUsed,
  preferredModel,
  preferredModelSetAt,
  onDocumentGenerated,
  autoGenerate,
  onAutoGenerateConsumed,
  defaultDocView,
}: { 
  transcription?: string;
  notes?: string;
  templateId: Id<"templates">;
  patientName: string;
  gender?: "male" | "female";
  sessionTitle: string;
  sessionId: Id<"recordingSessions">;
  userId: Id<"users">;
  existingDocument?: string;
  existingDocumentGeneratedAt?: number;
  existingModelUsed?: string;
  preferredModel?: string;
  preferredModelSetAt?: number;
  onDocumentGenerated: (hasDocument: boolean) => void;
  autoGenerate?: boolean;
  onAutoGenerateConsumed?: () => void;
  defaultDocView: "markdown" | "plain";
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState<string | null>(existingDocument || null);
  const [documentGeneratedAt, setDocumentGeneratedAt] = useState<number | null>(existingDocumentGeneratedAt || null);
  const [, setModelUsed] = useState<string | null>(existingModelUsed || null);
  const { data: modelsConfig } = useRQQuery<{ tiers: { thinking: string; small: string }; defaultTier: string; defaultTierSetAt: number | null }>({
    queryKey: ["/api/models/config"],
  });
  const adminDefaultTier = (modelsConfig?.defaultTier ?? "thinking") as "thinking" | "small";
  const ALLOWED_TIERS = new Set<string>(["thinking", "small"]);
  // Normalize any legacy stored model ID (e.g. a raw Opus/DeepSeek id) to a tier
  // before recency resolution so existing users keep working: legacy Opus ->
  // "thinking", else -> "small". Without this, a legacy id is "not allowed" and
  // collapses to the admin default instead of its tier equivalent.
  const normalizedPreferredModel = preferredModel
    ? normalizeTier(preferredModel)
    : preferredModel;
  const [selectedModel, setSelectedModel] = useState<string>(
    resolveEffectiveModel(normalizedPreferredModel, preferredModelSetAt, adminDefaultTier, modelsConfig?.defaultTierSetAt ?? null, ALLOWED_TIERS) ?? adminDefaultTier
  );
  const updatePreferredModel = useMutation(api.users.updatePreferredModel);
  const pendingPreferenceSaveRef = useRef<Promise<unknown> | null>(null);

  // Once config loads: resolve the effective tier, honouring recency between the
  // user's saved preference and an admin "reset everyone to default" action.
  useEffect(() => {
    if (!modelsConfig) return;
    const resolved = resolveEffectiveModel(
      normalizedPreferredModel,
      preferredModelSetAt,
      adminDefaultTier,
      modelsConfig.defaultTierSetAt ?? null,
      ALLOWED_TIERS,
    ) ?? adminDefaultTier;
    setSelectedModel(resolved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsConfig, preferredModel, preferredModelSetAt]);
  const [activeTocSection, setActiveTocSection] = useState<string | null>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const autoGenerateTriggeredRef = useRef(false);
  const { toast } = useToast();

  const documentSections = useMemo(() => {
    if (!generatedDocument) return [];
    const headingRegex = /^##\s+(.+)$/gm;
    const sections: { id: string; title: string }[] = [];
    let match;
    while ((match = headingRegex.exec(generatedDocument)) !== null) {
      const title = match[1].trim();
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      sections.push({ id, title });
    }
    return sections;
  }, [generatedDocument]);

  const parseSectionError = (
    content: string,
  ): { type: "system" | "missing" | "conflict"; message: string; cleanContent: string } | null => {
    const m = content.match(/^\s*<!--SECTION_ERROR:(system|missing|conflict):([\s\S]*?)-->\s*/);
    if (!m) return null;
    return {
      type: m[1] as "system" | "missing" | "conflict",
      message: m[2].replace(/--&gt;/g, "-->").trim(),
      cleanContent: content.slice(m[0].length),
    };
  };

  const sectionContentMap = useMemo(() => {
    if (!generatedDocument) return new Map<string, string>();
    const parts = generatedDocument.split(/^##\s+/m);
    const map = new Map<string, string>();
    for (const part of parts) {
      if (!part.trim()) continue;
      const newlineIdx = part.indexOf('\n');
      const title = newlineIdx >= 0 ? part.substring(0, newlineIdx).trim() : part.trim();
      const rawContent = newlineIdx >= 0 ? part.substring(newlineIdx + 1).trim() : "";
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const err = parseSectionError(rawContent);
      map.set(id, err ? err.cleanContent : rawContent);
    }
    return map;
  }, [generatedDocument]);

  const sectionErrorMap = useMemo(() => {
    if (!generatedDocument) return new Map<string, { type: "system" | "missing" | "conflict"; message: string }>();
    const parts = generatedDocument.split(/^##\s+/m);
    const map = new Map<string, { type: "system" | "missing" | "conflict"; message: string }>();
    for (const part of parts) {
      if (!part.trim()) continue;
      const newlineIdx = part.indexOf('\n');
      const title = newlineIdx >= 0 ? part.substring(0, newlineIdx).trim() : part.trim();
      const rawContent = newlineIdx >= 0 ? part.substring(newlineIdx + 1).trim() : "";
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const err = parseSectionError(rawContent);
      if (err) map.set(id, { type: err.type, message: err.message });
    }
    return map;
  }, [generatedDocument]);

  const sectionErrorMeta = (type: "system" | "missing" | "conflict") => {
    if (type === "system") {
      return {
        label: "System failure",
        description:
          "The AI service could not complete this section. The provider may be offline or returned an empty response. Try regenerating.",
      };
    }
    if (type === "missing") {
      return {
        label: "Missing information",
        description:
          "The transcription and notes do not contain enough information to generate this section.",
      };
    }
    return {
      label: "Conflicting instructions",
      description:
        "The section description and the tag-scoped rules contain instructions that cannot be reconciled.",
    };
  };

  const [copiedSectionId, setCopiedSectionId] = useState<string | null>(null);
  const [showToc, setShowToc] = useState(false);
  const [regeneratingSectionId, setRegeneratingSectionId] = useState<string | null>(null);
  const sectionViewModesStorageKey = `lrscribe.sectionViewModes.${sessionId}`;
  const readSectionViewModes = (key: string): Record<string, "markdown" | "plain"> => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const result: Record<string, "markdown" | "plain"> = {};
        for (const [k, v] of Object.entries(parsed)) {
          if (v === "markdown" || v === "plain") result[k] = v;
        }
        return result;
      }
    } catch {}
    return {};
  };
  const [sectionViewModes, setSectionViewModes] = useState<Record<string, "markdown" | "plain">>(() =>
    readSectionViewModes(sectionViewModesStorageKey),
  );
  const hydratedKeyRef = useRef<string>(sectionViewModesStorageKey);
  useEffect(() => {
    if (hydratedKeyRef.current !== sectionViewModesStorageKey) {
      hydratedKeyRef.current = sectionViewModesStorageKey;
      setSectionViewModes(readSectionViewModes(sectionViewModesStorageKey));
    }
  }, [sectionViewModesStorageKey]);
  useEffect(() => {
    if (hydratedKeyRef.current !== sectionViewModesStorageKey) return;
    try {
      window.localStorage.setItem(sectionViewModesStorageKey, JSON.stringify(sectionViewModes));
    } catch {}
  }, [sectionViewModes, sectionViewModesStorageKey]);
  const toggleSectionViewMode = (id: string, locked?: boolean) => {
    if (locked) return;
    setSectionViewModes((prev) => ({
      ...prev,
      [id]: prev[id] === "plain" ? "markdown" : "plain",
    }));
  };
  const setAllSectionsViewMode = (ids: string[], mode: "markdown" | "plain") => {
    setSectionViewModes((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        next[id] = mode;
      }
      return next;
    });
  };
  const sectionProgress = useSectionProgress();
  const [, setLocation] = useLocation();
  const createSectionChat = useMutation(api.chats.createSectionChat);
  const createSentenceDiscussionChat = useMutation(api.chats.createSentenceDiscussionChat);
  const openSentenceDiscussion = async (
    sectionTitle: string,
    sectionId: string,
    focusQuote: string,
  ) => {
    try {
      const chatId = await createSentenceDiscussionChat({
        userId,
        sessionId,
        templateId,
        sectionId,
        sectionTitle,
        focusQuote,
      });
      try {
        window.sessionStorage.setItem(
          "lrscribe.pendingSentenceChat",
          JSON.stringify({ chatId, focusQuote, sectionTitle }),
        );
      } catch {}
      setLocation(`/chat/${chatId}`);
    } catch (e: any) {
      toast({ title: "Couldn't open chat", description: e?.message, variant: "destructive" });
    }
  };
  const openSectionAssistant = async (sectionTitle: string, sectionId: string, focusQuote?: string | null) => {
    try {
      const chatId = await createSectionChat({
        userId,
        sessionId,
        templateId,
        sectionId,
        sectionTitle,
      });
      try {
        window.sessionStorage.setItem(
          "lrscribe.pendingSectionChat",
          JSON.stringify({ chatId, initialFocusQuote: focusQuote || null }),
        );
      } catch {}
      setLocation("/chat");
    } catch (e: any) {
      toast({ title: "Couldn't open assistant", description: e?.message, variant: "destructive" });
    }
  };
  const [selectionPopup, setSelectionPopup] = useState<{
    x: number;
    y: number;
    quote: string;
    sectionId: string;
    sectionTitle: string;
  } | null>(null);
  const documentContainerRef = useRef<HTMLDivElement>(null);
  const selectionPopupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectionPopup) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (selectionPopupRef.current?.contains(target)) return;
      if (documentContainerRef.current?.contains(target)) return;
      setSelectionPopup(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [selectionPopup]);

  type HoveredSentence = {
    sectionId: string;
    sectionTitle: string;
    text: string;
    blockKey: string;
    sStart: number;
    sEnd: number;
    rects: { top: number; left: number; width: number; height: number }[];
  };
  const [hoverSentence, setHoverSentence] = useState<HoveredSentence | null>(null);
  const [sentenceMenu, setSentenceMenu] = useState<{
    sectionId: string;
    sectionTitle: string;
    text: string;
    blockKey: string;
    sStart: number;
    sEnd: number;
    x: number;
    y: number;
  } | null>(null);
  const sentenceMenuRef = useRef<HTMLDivElement>(null);

  const findSentenceAtPoint = (
    clientX: number,
    clientY: number,
    wrapper: HTMLElement,
  ): {
    text: string;
    rects: { top: number; left: number; width: number; height: number }[];
    anchorRect: DOMRect;
    blockKey: string;
    sStart: number;
    sEnd: number;
  } | null => {
    const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    if (!target || !wrapper.contains(target)) return null;
    if (target.closest("h2, button, a, textarea, input, [data-no-sentence-hover]")) return null;
    const block = target.closest("p, li, td, blockquote, pre") as HTMLElement | null;
    if (!block || !wrapper.contains(block)) return null;
    const blocks = Array.from(wrapper.querySelectorAll("p, li, td, blockquote, pre"));
    const blockIdx = blocks.indexOf(block);
    const blockKey = `${block.tagName}#${blockIdx}`;

    const doc = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (
        x: number,
        y: number,
      ) => { offsetNode: Node; offset: number } | null;
    };
    let caretNode: Node | null = null;
    let caretOffset = 0;
    if (typeof doc.caretRangeFromPoint === "function") {
      const r = doc.caretRangeFromPoint(clientX, clientY);
      if (r) {
        caretNode = r.startContainer;
        caretOffset = r.startOffset;
      }
    } else if (typeof doc.caretPositionFromPoint === "function") {
      const p = doc.caretPositionFromPoint(clientX, clientY);
      if (p) {
        caretNode = p.offsetNode;
        caretOffset = p.offset;
      }
    }
    if (!caretNode) return null;

    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let fullText = "";
    const segs: { node: Text; start: number }[] = [];
    let caretGlobal = -1;
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const t = n as Text;
      segs.push({ node: t, start: fullText.length });
      if (t === caretNode) caretGlobal = fullText.length + caretOffset;
      fullText += t.data;
    }
    if (caretGlobal < 0 || !fullText.trim()) return null;

    const SENT = /[^.!?\n]*[.!?]+(?:\s+|$)|[^.!?\n]+(?=\n|$)/g;
    let sStart = -1;
    let sEnd = -1;
    let m: RegExpExecArray | null;
    while ((m = SENT.exec(fullText)) !== null) {
      if (m[0].length === 0) {
        SENT.lastIndex++;
        continue;
      }
      if (caretGlobal >= m.index && caretGlobal <= m.index + m[0].length) {
        sStart = m.index;
        sEnd = m.index + m[0].length;
        break;
      }
    }
    if (sStart < 0) return null;
    while (sStart < sEnd && /\s/.test(fullText[sStart])) sStart++;
    while (sEnd > sStart && /\s/.test(fullText[sEnd - 1])) sEnd--;
    if (sStart >= sEnd) return null;

    const findPos = (g: number) => {
      for (let i = segs.length - 1; i >= 0; i--) {
        if (segs[i].start <= g) {
          return {
            node: segs[i].node,
            offset: Math.min(g - segs[i].start, segs[i].node.data.length),
          };
        }
      }
      return { node: segs[0].node, offset: 0 };
    };
    const sp = findPos(sStart);
    const ep = findPos(sEnd);
    const range = document.createRange();
    try {
      range.setStart(sp.node, sp.offset);
      range.setEnd(ep.node, ep.offset);
    } catch {
      return null;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const clientRects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0,
    );
    if (clientRects.length === 0) return null;
    const rects = clientRects.map((r) => ({
      top: r.top - wrapperRect.top,
      left: r.left - wrapperRect.left,
      width: r.width,
      height: r.height,
    }));
    return {
      text: fullText.slice(sStart, sEnd).trim(),
      rects,
      anchorRect: clientRects[clientRects.length - 1],
      blockKey,
      sStart,
      sEnd,
    };
  };

  const handleSectionMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
    section: { id: string; title: string },
  ) => {
    const wrapper = e.currentTarget;
    const res = findSentenceAtPoint(e.clientX, e.clientY, wrapper);
    if (!res) {
      if (hoverSentence?.sectionId === section.id) setHoverSentence(null);
      return;
    }
    if (
      hoverSentence &&
      hoverSentence.sectionId === section.id &&
      hoverSentence.blockKey === res.blockKey &&
      hoverSentence.sStart === res.sStart &&
      hoverSentence.sEnd === res.sEnd &&
      hoverSentence.rects.length === res.rects.length
    ) {
      return;
    }
    setHoverSentence({
      sectionId: section.id,
      sectionTitle: section.title,
      text: res.text,
      blockKey: res.blockKey,
      sStart: res.sStart,
      sEnd: res.sEnd,
      rects: res.rects,
    });
  };

  const handleSectionClick = (
    e: React.MouseEvent<HTMLDivElement>,
    section: { id: string; title: string },
  ) => {
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.toString().trim()) return;
    const target = e.target as HTMLElement;
    if (target.closest("button, a, h2, textarea, input, [data-no-sentence-hover]")) return;
    const wrapper = e.currentTarget;
    const res = findSentenceAtPoint(e.clientX, e.clientY, wrapper);
    if (!res) {
      setSentenceMenu(null);
      return;
    }
    setSentenceMenu({
      sectionId: section.id,
      sectionTitle: section.title,
      text: res.text,
      blockKey: res.blockKey,
      sStart: res.sStart,
      sEnd: res.sEnd,
      x: res.anchorRect.left + res.anchorRect.width / 2,
      y: res.anchorRect.bottom + 6,
    });
  };

  useEffect(() => {
    if (!sentenceMenu) return;
    const onDown = (e: MouseEvent) => {
      if (sentenceMenuRef.current?.contains(e.target as Node)) return;
      setSentenceMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSentenceMenu(null);
    };
    const onScroll = () => setSentenceMenu(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [sentenceMenu]);

  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<string>("");
  const [historySectionTitle, setHistorySectionTitle] = useState<string | null>(null);
  const [focusModalSection, setFocusModalSection] = useState<{ title: string; id: string } | null>(null);
  const [focusText, setFocusText] = useState<string>("");
  const [learnModalSection, setLearnModalSection] = useState<{ title: string; id: string } | null>(null);
  const [learnMode, setLearnMode] = useState<"choose" | "improve">("choose");
  const [improvementText, setImprovementText] = useState<string>("");
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const addExampleToSection = useMutation(api.templates.addExampleToSection);
  const addExamplesToSections = useMutation(api.templates.addExamplesToSections);
  const appendToSectionDescription = useMutation(api.templates.appendToSectionDescription);
  const [isApprovingDocument, setIsApprovingDocument] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareSearch, setCompareSearch] = useState("");
  const [selectedCompareSession, setSelectedCompareSession] = useState<RecordingSession | null>(null);
  const [compareResult, setCompareResult] = useState<string | null>(null);
  const [compareResultOpen, setCompareResultOpen] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  
  const templateSections = useQuery(api.templates.getSections, { templateId, userId });
  const sectionHistoryEntityIds = useMemo(() => {
    if (!templateSections) return [] as string[];
    return templateSections.map(
      (s) => `${String(sessionId)}::${s.name.trim().toLowerCase()}`,
    );
  }, [templateSections, sessionId]);
  const sectionHistoryExistsMap = useQuery(
    api.editHistory.hasAnyForEntities,
    sectionHistoryEntityIds.length > 0
      ? {
          userId,
          entityType: "documentSection" as const,
          entityIds: sectionHistoryEntityIds,
        }
      : "skip",
  );
  const sectionsWithHistory = useMemo(() => {
    const set = new Set<string>();
    if (sectionHistoryExistsMap) {
      for (const [id, has] of Object.entries(
        sectionHistoryExistsMap as Record<string, boolean>,
      )) {
        if (has) set.add(id);
      }
    }
    return set;
  }, [sectionHistoryExistsMap]);
  const hasSectionHistory = (title: string) =>
    sectionsWithHistory.has(
      `${String(sessionId)}::${title.trim().toLowerCase()}`,
    );
  const stagedSectionRows = useQuery(
    api.stagedSectionChanges.listForSession,
    { userId, sessionId },
  );
  const globalRulesList = useQuery(api.globalRules.list, { userId });
  const clearStagedMut = useMutation(api.stagedSectionChanges.clear);
  const removeStagedEditMut = useMutation(api.stagedSectionChanges.removeEdit);
  const clearPreviewMut = useMutation(api.stagedSectionChanges.clearPreview);
  const applyEditDoc = useMutation(api.recordingSessions.applySectionContentEdit);
  const updateSectionMut = useMutation(api.templates.updateSection);
  const updateRuleMut = useMutation(api.globalRules.update);
  const removeRuleMut = useMutation(api.globalRules.remove);
  const createRuleMut = useMutation(api.globalRules.create);
  const [applyingStagedSectionId, setApplyingStagedSectionId] = useState<string | null>(null);
  const [stagedSectionErrors, setStagedSectionErrors] = useState<Record<string, Record<string, string>>>({});

  const stagedBySectionId = useMemo(() => {
    const map = new Map<string, any>();
    if (!stagedSectionRows || !templateSections) return map;
    const sectionsById = new Map(
      templateSections.map((s) => [String(s._id), s]),
    );
    for (const row of stagedSectionRows) {
      const sec = sectionsById.get(String(row.templateSectionId));
      if (!sec) continue;
      const slugId = sec.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      map.set(slugId, { row, section: sec });
    }
    return map;
  }, [stagedSectionRows, templateSections]);

  const discardStagedForSection = async (slugId: string) => {
    const entry = stagedBySectionId.get(slugId);
    if (!entry) return;
    try {
      await clearStagedMut({
        userId,
        sessionId,
        templateSectionId: entry.section._id,
      });
      toast({ title: "Staged changes discarded" });
    } catch (e: any) {
      toast({
        title: "Could not discard",
        description: e?.message,
        variant: "destructive",
      });
    }
  };

  const applyStagedForSection = async (slugId: string) => {
    const entry = stagedBySectionId.get(slugId);
    if (!entry) return;
    const row = entry.row;
    const sec = entry.section;
    if (!row.previewContent) {
      toast({
        title: "Preview required",
        description:
          "Open the section assistant and run Preview regenerate first.",
      });
      return;
    }
    setApplyingStagedSectionId(slugId);
    try {
      const result = await applyStagedEdits({
        row,
        section: sec,
        rules: globalRulesList ?? [],
        userId,
        sessionId,
        mutations: {
          updateSection: updateSectionMut,
          updateRule: updateRuleMut,
          removeRule: removeRuleMut,
          createRule: createRuleMut,
          applySectionContentEdit: applyEditDoc,
          removeStagedEdit: removeStagedEditMut,
          clearStaged: clearStagedMut,
          clearPreview: clearPreviewMut,
        },
      });
      setStagedSectionErrors((prev) => ({ ...prev, [slugId]: result.errors }));
      const numApplied = result.appliedEditIds.length;
      const numErrors = Object.keys(result.errors).length;
      if (numErrors === 0) {
        toast({
          title: "Staged changes applied",
          description: `${numApplied} edit${numApplied === 1 ? "" : "s"} committed.`,
        });
      } else {
        toast({
          title: numApplied > 0 ? "Some staged edits failed" : "Could not apply staged edits",
          description:
            "Open the section assistant to review the remaining errors and try again.",
          variant: "destructive",
        });
      }
    } catch (e: any) {
      toast({
        title: "Could not apply staged changes",
        description: e?.message,
        variant: "destructive",
      });
    } finally {
      setApplyingStagedSectionId(null);
    }
  };

  const allSessions = useQuery(api.recordingSessions.list, { userId });
  const liveSession = useQuery(api.recordingSessions.get, { id: sessionId, userId });
  const generationStatus = (liveSession as { generationStatus?: any } | null | undefined)?.generationStatus;
  let generationProgressNode: ReactNode = null;
  const liveSessionRef = useRef(liveSession);
  useEffect(() => { liveSessionRef.current = liveSession; }, [liveSession]);

  // Server-driven generation detection — drives the progress bar and button guard
  // independently of the local isGenerating flag so progress survives disconnects
  // and session switches.
  const GENERATION_FRESHNESS_MS = 5 * 60 * 1000; // 5 min — max run duration
  const TERMINAL_PHASES = new Set(["done", "error"]);
  const hasNonTerminalStatus = !!(generationStatus && !TERMINAL_PHASES.has(generationStatus.phase));
  const serverGenerationActive = !!(
    hasNonTerminalStatus &&
    generationStatus?.updatedAt &&
    Date.now() - generationStatus.updatedAt < GENERATION_FRESHNESS_MS
  );
  // Tick every 3 s so staleness is re-evaluated even when Convex sends no new updates
  // (i.e., server crashed mid-run and generationStatus froze). Keyed to
  // serverGenerationActive so the interval stops automatically once the run is
  // considered stale — no indefinite timer for frozen non-terminal statuses.
  const [, setStalenessTick] = useState(0);
  useEffect(() => {
    if (!serverGenerationActive) return;
    const id = setInterval(() => setStalenessTick((t) => t + 1), 3000);
    return () => clearInterval(id);
  }, [serverGenerationActive]);

  type SourceMapEntry = { outputQuote: string; transcriptQuotes: string[] };
  type SessionWithSourceMap = { sourceMap?: SourceMapEntry[] };
  const sourceMap: SourceMapEntry[] | undefined =
    (liveSession as SessionWithSourceMap | null | undefined)?.sourceMap;
  const [sourcesPanelOpen, setSourcesPanelOpen] = useState(false);
  const [selectedTranscriptQuote, setSelectedTranscriptQuote] = useState<string | null>(null);

  const normalizeQuote = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const quoteAppearsInText = (haystack: string, quote: string): boolean => {
    if (!quote.trim()) return false;
    const pattern = escapeRegex(quote).replace(/\s+/g, "\\s+");
    try {
      return new RegExp(pattern, "i").test(haystack);
    } catch {
      return false;
    }
  };

  const combinedSourceText = useMemo(() => {
    const parts: string[] = [];
    if (transcription) parts.push(transcription);
    if (notes) parts.push(notes);
    return parts.join("\n\n");
  }, [transcription, notes]);

  const resolvableTranscriptQuotes = useMemo(() => {
    const resolvable = new Set<string>();
    if (!sourceMap || !combinedSourceText) return resolvable;
    for (const e of sourceMap) {
      for (const q of e.transcriptQuotes) {
        const n = normalizeQuote(q);
        if (!n || resolvable.has(n)) continue;
        if (quoteAppearsInText(combinedSourceText, q)) resolvable.add(n);
      }
    }
    return resolvable;
  }, [sourceMap, combinedSourceText]);

  const allTranscriptQuotes = useMemo(() => {
    if (!sourceMap) return [] as string[];
    const seen = new Map<string, string>();
    for (const e of sourceMap) {
      for (const q of e.transcriptQuotes) {
        const n = normalizeQuote(q);
        if (n && resolvableTranscriptQuotes.has(n) && !seen.has(n)) seen.set(n, q);
      }
    }
    return Array.from(seen.values());
  }, [sourceMap, resolvableTranscriptQuotes]);

  const renderTranscriptWithHighlights = (text: string): ReactNode => {
    if (!text) return null;
    const escaped = allTranscriptQuotes
      .map((q) => q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+"))
      .filter((q) => q.length > 0);
    if (escaped.length === 0) {
      return <div className="whitespace-pre-wrap text-sm leading-relaxed">{text}</div>;
    }
    const re = new RegExp(`(${escaped.join("|")})`, "gi");
    const parts: ReactNode[] = [];
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    let key = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > lastIdx) parts.push(<Fragment key={`t-${key++}`}>{text.slice(lastIdx, m.index)}</Fragment>);
      const match = m[0];
      const norm = normalizeQuote(match);
      const isSelected = selectedTranscriptQuote === norm;
      const cls = isSelected
        ? "bg-blue-300 dark:bg-blue-600/70 text-black dark:text-white"
        : "bg-yellow-100 dark:bg-yellow-900/30";
      parts.push(
        <mark
          key={`t-${key++}`}
          onClick={() =>
            setSelectedTranscriptQuote((prev) => (prev === norm ? null : norm))
          }
          className={`cursor-pointer transition-colors px-0.5 rounded ${cls}`}
          data-testid={`transcript-source-${key}`}
        >
          {match}
        </mark>,
      );
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) parts.push(<Fragment key={`t-${key++}`}>{text.slice(lastIdx)}</Fragment>);
    return <div className="whitespace-pre-wrap text-sm leading-relaxed">{parts}</div>;
  };

  const waitForServerSavedDocument = async (
    predicate: (s: any) => boolean,
    timeoutMs: number = 180000,
    intervalMs: number = 2000
  ): Promise<any | null> => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const s = liveSessionRef.current;
      if (s && predicate(s)) return s;
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
  };

  const staticSectionMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!templateSections) return map;
    for (const ts of templateSections) {
      if (ts.adhereToFormatting && ts.formatTemplate) {
        const placeholders = extractPlaceholders(ts.formatTemplate);
        if (placeholders.length > 0) {
          const id = ts.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
          map.set(id, ts.formatTemplate);
        }
      }
    }
    return map;
  }, [templateSections]);

  const handleStaticSectionEdit = async (sectionTitle: string, sectionId: string, newContent: string) => {
    if (!generatedDocument) return;
    const sectionRegex = new RegExp(
      `(## ${sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\n\\n?)([\\s\\S]*?)(?=\\n## |$)`
    );
    const updatedDocument = generatedDocument.replace(sectionRegex, `## ${sectionTitle}\n\n${newContent}`);
    setGeneratedDocument(updatedDocument);
    try {
      await applyEditDoc({
        id: sessionId,
        userId,
        sectionTitle,
        newContent,
        provenance: { source: "user" },
      });
    } catch (error) {
      console.error("Failed to save edit:", error);
      toast({ title: "Save failed", description: "Could not save the edit.", variant: "destructive" });
    }
  };

  const copySectionContent = async (id: string, mode: "markdown" | "plain" = "markdown") => {
    const content = sectionContentMap.get(id);
    if (content) {
      const output = mode === "plain" ? markdownToPlainText(content) : content;
      await navigator.clipboard.writeText(output);
      setCopiedSectionId(id);
      setTimeout(() => setCopiedSectionId(null), 2000);
    }
  };

  const handleRegenerateSection = async (sectionTitle: string, sectionId: string, focusInstruction?: string) => {
    if (!templateSections || !generatedDocument) return;
    const section = templateSections.find((s) => s.name === sectionTitle);
    if (!section) return;

    if (pendingPreferenceSaveRef.current) {
      try {
        await pendingPreferenceSaveRef.current;
      } catch {
        // preference save failed; continue with best-effort model
      }
      pendingPreferenceSaveRef.current = null;
    }

    const stagedEntry = stagedBySectionId.get(sectionId);
    if (stagedEntry?.row?.previewContent) {
      const ok = window.confirm(
        "This section has a staged preview shown. Regenerating will discard the staged changes for this section. Continue?",
      );
      if (!ok) return;
      try {
        await clearStagedMut({
          userId,
          sessionId,
          templateSectionId: stagedEntry.section._id,
        });
      } catch {
        // best-effort; proceed regardless
      }
    }

    setRegeneratingSectionId(sectionId);
    sectionProgress.set(sectionId, "received");
    const previousDocument = generatedDocument;
    const escapedTitle = sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const originalBlockRegex = new RegExp(
      `## ${escapedTitle}\\n\\n?[\\s\\S]*?(?=\\n## |$)`
    );
    const originalBlockMatch = generatedDocument.match(originalBlockRegex);
    const originalSectionBlock = originalBlockMatch ? originalBlockMatch[0] : undefined;
    try {
      let result: { content: string };
      try {
        result = await runWithProgress({
          url: "/api/regenerate-section",
          body: {
            transcription: transcription || undefined,
            notes: notes || undefined,
            section,
            patientName,
            gender,
            sessionTitle,
            sessionId,
            focusText: focusInstruction || undefined,
            originalSectionBlock,
          },
          onStage: (s) => sectionProgress.set(sectionId, s),
        });
      } catch (err) {
        const runErr = err instanceof RunError ? err : null;
        const response = runErr?.response;
        if (response && response.status === 401) {
          toast({
            title: "Session expired",
            description: "Please sign in again to continue. Redirecting…",
            variant: "destructive",
          });
          setTimeout(() => { window.location.href = "/api/login"; }, 1200);
          return;
        }
        // Treat genuine network drops AND abrupt SSE stream terminations as
        // recoverable — fall back to Convex live sync. Explicit HTTP errors
        // and server-sent error events still fail fast with a normal toast.
        const isRecoverableDrop =
          !runErr || runErr.kind === "network_error" || runErr.kind === "stream_drop";
        if (!isRecoverableDrop) {
          throw err;
        }
        sectionProgress.set(sectionId, "recovering");
        toast({
          title: "Still working…",
          description: "The connection dropped, but regeneration is continuing on the server. We'll update the section as soon as it's ready.",
        });
        const recovered = await waitForServerSavedDocument(
          (s) => !!s.generatedDocument && s.generatedDocument !== previousDocument,
          180000,
        );
        if (recovered) {
          setGeneratedDocument(recovered.generatedDocument);
          toast({ title: "Section regenerated", description: `"${sectionTitle}" has been updated.` });
          return;
        }
        throw err;
      }

      const content = result.content;
      const newBlock = `## ${sectionTitle}\n\n${content}`;
      let updatedDocument: string;
      if (originalSectionBlock && generatedDocument.includes(originalSectionBlock)) {
        updatedDocument = generatedDocument.replace(originalSectionBlock, newBlock);
      } else {
        const sectionRegex = new RegExp(
          `(## ${escapedTitle}\\n\\n?)([\\s\\S]*?)(?=\\n## |$)`
        );
        updatedDocument = generatedDocument.replace(sectionRegex, newBlock);
      }
      setGeneratedDocument(updatedDocument);

      // Keep the placeholder visible until the Convex reactive subscription
      // has delivered a session snapshot containing the freshly generated
      // section block. Without this gate, a stale Convex snapshot arriving
      // after our optimistic splice briefly overwrites the new content with
      // the old text before settling, causing a visible flash.
      sectionProgress.set(sectionId, "saving_changes");
      const confirmed = await waitForServerSavedDocument(
        (s) => !!s?.generatedDocument && s.generatedDocument.includes(newBlock),
        15000,
        300,
      );
      if (!confirmed) {
        console.warn(
          `Regenerate-section: timed out waiting for Convex to confirm new content for "${sectionTitle}"; clearing placeholder anyway.`,
        );
      }

      toast({ title: "Section regenerated", description: `"${sectionTitle}" has been updated.` });
    } catch (error) {
      console.error("Section regeneration error:", error);
      toast({ title: "Regeneration failed", description: "There was an error regenerating this section.", variant: "destructive" });
    } finally {
      setRegeneratingSectionId(null);
      sectionProgress.clear(sectionId);
    }
  };

  const openFocusModal = (title: string, id: string) => {
    setFocusText("");
    setFocusModalSection({ title, id });
  };

  const openLearnModal = (title: string, id: string) => {
    setLearnMode("choose");
    setImprovementText("");
    setLearnModalSection({ title, id });
  };

  const findTemplateSection = (title: string) => {
    if (!templateSections) return undefined;
    return templateSections.find((s) => s.name === title);
  };

  const handleLooksGood = async () => {
    if (!learnModalSection) return;
    const section = findTemplateSection(learnModalSection.title);
    if (!section) {
      toast({ title: "Could not save", description: "Matching template section not found.", variant: "destructive" });
      setLearnModalSection(null);
      return;
    }
    const content = sectionContentMap.get(learnModalSection.id) || "";
    if (!content.trim()) {
      toast({ title: "Nothing to save", description: "This section is empty.", variant: "destructive" });
      return;
    }
    setIsSavingFeedback(true);
    try {
      await addExampleToSection({ id: section._id, userId, example: content });
      toast({
        title: "Saved as example",
        description: `Future "${learnModalSection.title}" sections will learn from this version.`,
      });
      setLearnModalSection(null);
    } catch (error) {
      console.error("Failed to add example:", error);
      toast({ title: "Save failed", description: "Could not save this as an example.", variant: "destructive" });
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleApproveDocument = async () => {
    if (!templateSections || !generatedDocument) return;
    const payload: { sectionId: Id<"templateSections">; example: string }[] = [];
    for (const section of documentSections) {
      const content = sectionContentMap.get(section.id) || "";
      if (!content.trim()) continue;
      const ts = templateSections.find((t) => t.name === section.title);
      if (!ts) continue;
      payload.push({ sectionId: ts._id, example: content });
    }
    if (payload.length === 0) {
      toast({ title: "Nothing to save", description: "No matching sections found to save as examples.", variant: "destructive" });
      return;
    }
    setIsApprovingDocument(true);
    try {
      const added = await addExamplesToSections({ userId, templateId, examples: payload });
      toast({
        title: "Document approved",
        description: `Saved ${added} section${added === 1 ? "" : "s"} as examples for future generations.`,
      });
    } catch (error) {
      console.error("Failed to approve document:", error);
      toast({ title: "Save failed", description: "Could not save sections as examples.", variant: "destructive" });
    } finally {
      setIsApprovingDocument(false);
    }
  };

  const handleSubmitImprovement = async () => {
    if (!learnModalSection) return;
    const section = findTemplateSection(learnModalSection.title);
    if (!section) {
      toast({ title: "Could not save", description: "Matching template section not found.", variant: "destructive" });
      setLearnModalSection(null);
      return;
    }
    if (!improvementText.trim()) return;
    setIsSavingFeedback(true);
    try {
      await appendToSectionDescription({ id: section._id, userId, correction: improvementText });
      toast({
        title: "Feedback saved",
        description: `Your correction was added to the "${learnModalSection.title}" section instructions.`,
      });
      setLearnModalSection(null);
      setImprovementText("");
    } catch (error) {
      console.error("Failed to append correction:", error);
      toast({ title: "Save failed", description: "Could not save your correction.", variant: "destructive" });
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleFocusRegenerate = async () => {
    if (!focusModalSection) return;
    const { title, id } = focusModalSection;
    setFocusModalSection(null);
    await handleRegenerateSection(title, id, focusText);
    setFocusText("");
  };

  const scrollToDocSection = (id: string) => {
    const el = sectionRefs.current.get(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveTocSection(id);
    }
  };

  useEffect(() => {
    if (existingDocument) {
      setGeneratedDocument(existingDocument);
      setDocumentGeneratedAt(existingDocumentGeneratedAt || null);
      setModelUsed(existingModelUsed || null);
      onDocumentGenerated(true);
    }
  }, [existingDocument, existingDocumentGeneratedAt, existingModelUsed, onDocumentGenerated]);

  const handleGenerate = async () => {
    if (!templateSections || templateSections.length === 0) {
      toast({ 
        title: "No sections", 
        description: "This template has no sections defined.", 
        variant: "destructive" 
      });
      return;
    }

    if (pendingPreferenceSaveRef.current) {
      try {
        await pendingPreferenceSaveRef.current;
      } catch {
        // preference save failed; continue with best-effort model
      }
      pendingPreferenceSaveRef.current = null;
    }

    setIsGenerating(true);
    const clickedAt = Date.now();
    const previousGeneratedAt = documentGeneratedAt || 0;
    const slugify = (name: string) =>
      name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const trackedSectionIds = new Set<string>();
    const clearAllStages = () => {
      trackedSectionIds.forEach((id) => sectionProgress.clear(id));
      trackedSectionIds.clear();
    };
    try {
      let response: Response;
      try {
        response = await fetch("/api/generate-document", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream, application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            transcription: transcription || undefined,
            notes: notes || undefined,
            templateSections,
            patientName,
            gender,
            sessionTitle,
            sessionId,
            model: selectedModel,
          }),
        });
      } catch (networkErr) {
        console.warn("generate-document fetch dropped, awaiting Convex:", networkErr);
        toast({
          title: "Still working…",
          description: "The connection dropped, but generation is continuing on the server. We'll show the document as soon as it's ready.",
        });
        const recovered = await waitForServerSavedDocument(
          (s) => !!s.generatedDocument && (s.documentGeneratedAt || 0) >= clickedAt && (s.documentGeneratedAt || 0) > previousGeneratedAt
        );
        if (recovered) {
          setGeneratedDocument(recovered.generatedDocument);
          setDocumentGeneratedAt(recovered.documentGeneratedAt);
          setModelUsed(recovered.modelUsed || null);
          onDocumentGenerated(true);
          toast({ title: "Document generated", description: "Your document has been created and saved." });
          return;
        }
        throw networkErr;
      }

      if (response.status === 401) {
        toast({
          title: "Session expired",
          description: "Please sign in again to continue. Redirecting…",
          variant: "destructive",
        });
        setTimeout(() => { window.location.href = "/api/login"; }, 1200);
        return;
      }

      if (!response.ok) {
        const recovered = await waitForServerSavedDocument(
          (s) => !!s.generatedDocument && (s.documentGeneratedAt || 0) >= clickedAt && (s.documentGeneratedAt || 0) > previousGeneratedAt,
          10000
        );
        if (recovered) {
          setGeneratedDocument(recovered.generatedDocument);
          setDocumentGeneratedAt(recovered.documentGeneratedAt);
          setModelUsed(recovered.modelUsed || null);
          onDocumentGenerated(true);
          toast({ title: "Document generated", description: "Your document has been created and saved." });
          return;
        }
        throw new Error("Failed to generate document");
      }

      const ctype = response.headers.get("content-type") || "";
      let document: string;
      let newGeneratedAt: number;
      let model: string | undefined;

      if (ctype.includes("text/event-stream") && response.body) {
        type DocPayload = {
          document?: unknown;
          documentGeneratedAt?: unknown;
          modelUsed?: unknown;
          sectionName?: unknown;
          message?: unknown;
        };
        const sectionStageEvents = ["preparing", "sending_to_llm", "awaiting_llm", "saving"] as const;
        type SectionStageEvent = (typeof sectionStageEvents)[number];
        const isSectionStageEvent = (e: string): e is SectionStageEvent =>
          (sectionStageEvents as readonly string[]).includes(e);
        const clearSectionStage = (name: string) => {
          const id = slugify(name);
          if (trackedSectionIds.has(id)) {
            trackedSectionIds.delete(id);
            sectionProgress.clear(id);
          }
        };

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let finalDoc: { document: string; documentGeneratedAt: number; modelUsed?: string } | null = null;
        let streamError: string | null = null;
        const handle = (event: string, dataText: string) => {
          let data: DocPayload = {};
          try {
            const parsed: unknown = dataText ? JSON.parse(dataText) : {};
            if (parsed && typeof parsed === "object") data = parsed as DocPayload;
          } catch {}
          if (event === "done") {
            finalDoc = {
              document: typeof data.document === "string" ? data.document : "",
              documentGeneratedAt:
                typeof data.documentGeneratedAt === "number" ? data.documentGeneratedAt : Date.now(),
              modelUsed: typeof data.modelUsed === "string" ? data.modelUsed : undefined,
            };
          } else if (event === "error") {
            streamError = typeof data.message === "string" ? data.message : "Stream error";
          } else if (event === "speaker_labeling_failed") {
            toast({
              title: "Speaker labeling unavailable",
              description: "The document was generated from the unprocessed transcript because speaker labeling could not complete.",
            });
          } else if (event === "section_done") {
            const name = typeof data.sectionName === "string" ? data.sectionName : "";
            if (name) clearSectionStage(name);
          } else if (isSectionStageEvent(event)) {
            const name = typeof data.sectionName === "string" ? data.sectionName : "";
            if (name) {
              const id = slugify(name);
              trackedSectionIds.add(id);
              sectionProgress.set(id, event);
            }
          }
        };
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let sep: number;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const raw = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            let event = "message";
            const dataLines: string[] = [];
            for (const line of raw.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
            }
            handle(event, dataLines.join("\n"));
          }
        }
        if (streamError) {
          const e = new Error(streamError) as Error & { kind?: string };
          e.kind = "stream_error";
          throw e;
        }
        if (!finalDoc) {
          // Abrupt SSE termination without `done`. Surface as a recoverable
          // drop so the catch block can fall back to Convex live sync.
          const e = new Error("Stream ended without a done event") as Error & { kind?: string };
          e.kind = "stream_drop";
          throw e;
        }
        const doc: { document: string; documentGeneratedAt: number; modelUsed?: string } = finalDoc;
        document = doc.document;
        newGeneratedAt = doc.documentGeneratedAt;
        model = doc.modelUsed;
      } else {
        const json = (await response.json()) as {
          document?: unknown;
          documentGeneratedAt?: unknown;
          modelUsed?: unknown;
        };
        document = typeof json.document === "string" ? json.document : "";
        newGeneratedAt = typeof json.documentGeneratedAt === "number" ? json.documentGeneratedAt : Date.now();
        model = typeof json.modelUsed === "string" ? json.modelUsed : undefined;
      }

      // Show a "Saving changes…" indicator on every section while we wait
      // for the Convex live subscription to reflect the freshly saved
      // document. This keeps a progress bar visible during the gap between
      // the server's `done` event and the Convex confirmation.
      if (templateSections) {
        for (const ts of templateSections) {
          const id = slugify(ts.name);
          trackedSectionIds.add(id);
          sectionProgress.set(id, "saving_changes");
        }
      }
      await waitForServerSavedDocument(
        (s) =>
          !!s.generatedDocument &&
          (s.documentGeneratedAt || 0) >= newGeneratedAt,
        15000,
        300,
      );

      setGeneratedDocument(document);
      setDocumentGeneratedAt(newGeneratedAt);
      setModelUsed(model || null);

      onDocumentGenerated(true);
      toast({ title: "Document generated", description: "Your document has been created and saved." });
    } catch (error) {
      console.error("Document generation error:", error);
      const kind = (error as { kind?: string } | null)?.kind;
      if (kind === "stream_drop") {
        // SSE terminated mid-stream — generation likely continues on the
        // server. Switch every active section overlay to a "recovering"
        // label and wait for Convex to surface the final document.
        trackedSectionIds.forEach((id) => sectionProgress.set(id, "recovering"));
        toast({
          title: "Still working…",
          description: "The connection dropped, but generation is continuing on the server. We'll show the document as soon as it's ready.",
        });
        const recovered = await waitForServerSavedDocument(
          (s) => !!s.generatedDocument && (s.documentGeneratedAt || 0) >= clickedAt && (s.documentGeneratedAt || 0) > previousGeneratedAt,
          180000,
        );
        if (recovered) {
          setGeneratedDocument(recovered.generatedDocument);
          setDocumentGeneratedAt(recovered.documentGeneratedAt);
          setModelUsed(recovered.modelUsed || null);
          onDocumentGenerated(true);
          toast({ title: "Document generated", description: "Your document has been created and saved." });
          return;
        }
      }
      toast({
        title: "Generation failed",
        description: "There was an error generating the document.",
        variant: "destructive"
      });
    } finally {
      clearAllStages();
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!autoGenerate) {
      autoGenerateTriggeredRef.current = false;
    }
  }, [autoGenerate]);

  useEffect(() => {
    if (autoGenerate && !autoGenerateTriggeredRef.current && templateSections && templateSections.length > 0 && !isGenerating && !serverGenerationActive) {
      autoGenerateTriggeredRef.current = true;
      if (onAutoGenerateConsumed) onAutoGenerateConsumed();
      handleGenerate();
    }
  }, [autoGenerate, templateSections, isGenerating, serverGenerationActive]);

  const stripErrorMarkers = (doc: string | null | undefined): string => {
    if (!doc) return "";
    return doc.replace(/<!--SECTION_ERROR:(?:system|missing|conflict):[\s\S]*?-->\s*/g, "");
  };


  const handleExportPdf = () => {
    if (!generatedDocument) return;
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const cleanMarkdown = stripErrorMarkers(generatedDocument);
    let bodyHtml: string;
    try {
      bodyHtml = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanMarkdown}</ReactMarkdown>,
      );
    } catch (error) {
      console.error("PDF export render error:", error);
      toast({
        title: "Export failed",
        description: "There was an error preparing the document for export.",
        variant: "destructive",
      });
      return;
    }

    const safeTitle = (sessionTitle || "Document").replace(/[^a-z0-9\-_ ]/gi, "").trim() || "Document";
    const generatedLabel = documentGeneratedAt
      ? new Date(documentGeneratedAt).toLocaleString()
      : new Date().toLocaleString();

    const docHtml = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(safeTitle)}</title>
    <style>
      @page { margin: 18mm 16mm; }
      * { box-sizing: border-box; }
      body {
        font-family: Inter, "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        color: #1a1a1a;
        font-size: 12px;
        line-height: 1.6;
        margin: 0;
      }
      .doc-header {
        border-bottom: 2px solid #1a1a1a;
        padding-bottom: 12px;
        margin-bottom: 20px;
      }
      .doc-header h1 {
        font-size: 20px;
        margin: 0 0 6px 0;
        line-height: 1.3;
      }
      .doc-meta { font-size: 11px; color: #555; }
      .doc-meta span { margin-right: 14px; }
      .doc-body h1 { font-size: 17px; margin: 18px 0 8px; }
      .doc-body h2 { font-size: 15px; margin: 16px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #ddd; }
      .doc-body h3 { font-size: 13px; margin: 14px 0 6px; }
      .doc-body p { margin: 0 0 10px; }
      .doc-body ul, .doc-body ol { margin: 0 0 10px; padding-left: 22px; }
      .doc-body li { margin: 0 0 4px; }
      .doc-body table { border-collapse: collapse; width: 100%; margin: 0 0 12px; }
      .doc-body th, .doc-body td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
      .doc-body th { background: #f3f4f6; }
      .doc-body blockquote { margin: 0 0 10px; padding: 4px 12px; border-left: 3px solid #ccc; color: #444; }
      .doc-body code { font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace; font-size: 11px; background: #f3f4f6; padding: 1px 4px; border-radius: 3px; }
      .doc-body pre { background: #f3f4f6; padding: 10px; border-radius: 4px; overflow-x: auto; }
      .doc-body pre code { background: none; padding: 0; }
      .doc-body h1, .doc-body h2, .doc-body h3 { page-break-after: avoid; }
      .doc-body tr, .doc-body img { page-break-inside: avoid; }
    </style>
  </head>
  <body>
    <div class="doc-header">
      <h1>${escapeHtml(sessionTitle || "Generated Document")}</h1>
      <div class="doc-meta">
        ${patientName ? `<span><strong>Patient:</strong> ${escapeHtml(patientName)}</span>` : ""}
        <span><strong>Generated:</strong> ${escapeHtml(generatedLabel)}</span>
      </div>
    </div>
    <div class="doc-body">${bodyHtml}</div>
  </body>
</html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentWindow?.document;
    const frameWin = iframe.contentWindow;
    if (!frameDoc || !frameWin) {
      iframe.remove();
      toast({
        title: "Export failed",
        description: "Could not open the print dialog. Please try again.",
        variant: "destructive",
      });
      return;
    }

    frameDoc.open();
    frameDoc.write(docHtml);
    frameDoc.close();

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      iframe.remove();
    };

    // Remove the iframe once the print dialog closes, with a long fallback in
    // case `afterprint` never fires (browser/print-driver differences).
    frameWin.onafterprint = cleanup;
    const fallbackTimer = window.setTimeout(cleanup, 60000);

    const triggerPrint = () => {
      try {
        frameWin.focus();
        frameWin.print();
      } catch (error) {
        console.error("PDF export print error:", error);
        toast({
          title: "Export failed",
          description: "Could not open the print dialog. Please try again.",
          variant: "destructive",
        });
        window.clearTimeout(fallbackTimer);
        cleanup();
      }
    };

    // Give the iframe a moment to render the content before printing.
    window.setTimeout(triggerPrint, 350);
  };

  const otherSessions = (allSessions ?? []).filter((s) => s._id !== sessionId);
  const filteredCompareSessions = compareSearch.trim()
    ? otherSessions.filter((s) =>
        `${s.title} ${s.patientName}`.toLowerCase().includes(compareSearch.toLowerCase())
      )
    : otherSessions;

  const handleCompare = async () => {
    if (!selectedCompareSession || !generatedDocument) return;
    setIsComparing(true);
    setCompareOpen(false);
    try {
      const response = await fetch("/api/compare-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentSession: {
            title: sessionTitle,
            patientName,
            generatedDocument: stripErrorMarkers(generatedDocument),
            transcription,
            notes,
          },
          compareSession: {
            title: selectedCompareSession.title,
            patientName: selectedCompareSession.patientName,
            generatedDocument: stripErrorMarkers(selectedCompareSession.generatedDocument),
            transcription: selectedCompareSession.transcription,
            notes: selectedCompareSession.notes,
          },
          model: selectedModel,
        }),
      });
      if (!response.ok) throw new Error("Failed to compare sessions");
      const { result } = await response.json();
      setCompareResult(result);
      setCompareResultOpen(true);
    } catch (error) {
      console.error("Compare error:", error);
      toast({ title: "Comparison failed", description: "There was an error comparing the sessions.", variant: "destructive" });
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Generated Document Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Generated Document
          </h3>
          <div className="flex gap-2">
            <div className="flex flex-col items-end gap-1.5">
              <Select
                value={selectedModel}
                onValueChange={(value) => {
                  setSelectedModel(value);
                  pendingPreferenceSaveRef.current = updatePreferredModel({ id: userId, preferredModel: value });
                }}
                disabled={isGenerating}
                data-testid="select-model"
              >
                <SelectTrigger className="h-7 text-xs w-48" data-testid="select-trigger-model">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="thinking" data-testid="select-item-model-thinking">
                    <span className="font-medium">Thinking</span>
                    {modelsConfig && <span className="text-muted-foreground ml-1 text-[10px] font-mono">{modelsConfig.tiers.thinking}</span>}
                  </SelectItem>
                  <SelectItem value="small" data-testid="select-item-model-small">
                    <span className="font-medium">Small</span>
                    {modelsConfig && <span className="text-muted-foreground ml-1 text-[10px] font-mono">{modelsConfig.tiers.small}</span>}
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                {generatedDocument && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleApproveDocument}
                    disabled={isApprovingDocument || !templateSections}
                    data-testid="button-approve-document"
                  >
                    {isApprovingDocument ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <ThumbsUp className="w-3 h-3 mr-1" />
                        Document Looks Good
                      </>
                    )}
                  </Button>
                )}
                {generatedDocument && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleExportPdf}
                    data-testid="button-export-pdf"
                  >
                    <FileDown className="w-3 h-3 mr-1" />
                    Export PDF
                  </Button>
                )}
                {generatedDocument && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setCompareSearch("");
                      setSelectedCompareSession(null);
                      setCompareOpen(true);
                    }}
                    disabled={isComparing}
                    data-testid="button-compare-document"
                  >
                    {isComparing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        Comparing...
                      </>
                    ) : (
                      <>
                        <GitCompare className="w-3 h-3 mr-1" />
                        Compare
                      </>
                    )}
                  </Button>
                )}
                <Button 
                  size="sm" 
                  onClick={handleGenerate}
                  disabled={isGenerating || serverGenerationActive || !templateSections}
                  data-testid="button-generate-document"
                >
                  {isGenerating || serverGenerationActive ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      {serverGenerationActive && !isGenerating ? "In progress…" : "Generating..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 mr-1" />
                      {generatedDocument ? "Regenerate" : "Generate Document"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {(() => {
          // Show progress bar when generation is active locally OR driven by the
          // server-persisted generationStatus (survives disconnects & session switches).
          const showProgress = isGenerating || serverGenerationActive;
          // Show an error note when the server briefly signals failure (phase=error).
          const showServerError = !isGenerating && !serverGenerationActive && generationStatus?.phase === "error";

          if (showServerError) {
            generationProgressNode = (
              <div
                className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/20"
                data-testid="generation-error"
              >
                <p className="text-sm text-destructive font-medium">Document generation failed.</p>
                <p className="text-xs text-destructive/80 mt-0.5">It is safe to try again.</p>
              </div>
            );
            return null;
          }

          if (!showProgress) {
            generationProgressNode = null;
            return null;
          }
          const phase = generationStatus?.phase;
          const sectionName = generationStatus?.sectionName;
          const sectionIndex = generationStatus?.sectionIndex;
          const sectionsTotal = generationStatus?.sectionsTotal;
          const verifierAttempt = generationStatus?.verifierAttempt;
          const stepsCompleted = generationStatus?.stepsCompleted ?? 0;
          const stepsTotal = generationStatus?.stepsTotal ?? 0;
          const progressPct = stepsTotal > 0
            ? Math.min(100, Math.round((stepsCompleted / stepsTotal) * 100))
            : 0;
          let label = "Preparing…";
          if (phase === "preprocessing_sentences") label = "Splitting transcript into sentences…";
          else if (phase === "preprocessing_speakers") label = "Identifying speakers…";
          else if (phase === "preprocessing_backchannels") label = "Re-adding short replies…";
          else if (phase === "preprocessing_finalizing") label = "Finalizing speaker labels…";
          else if (phase === "preprocessing_cached") label = "Speaker labels up to date ✓";
          else if (phase === "gathering") label = "Gathering context…";
          else if (phase === "drafting") {
            label = sectionName && sectionIndex && sectionsTotal
              ? `Drafting ${sectionName} (${sectionIndex} of ${sectionsTotal})`
              : "Drafting sections…";
          } else if (phase === "verifying") {
            const base = sectionName && sectionIndex && sectionsTotal
              ? `Verifying ${sectionName} (${sectionIndex} of ${sectionsTotal})`
              : "Verifying sections";
            label = verifierAttempt && verifierAttempt > 1
              ? `${base} — retry ${verifierAttempt}…`
              : `${base}…`;
          } else if (phase === "assembling") label = "Assembling document…";
          else if (phase === "done") label = "Finishing up…";
          generationProgressNode = (
            <div className="mb-4" data-testid="generation-progress">
              <Progress value={progressPct} data-testid="progress-generation" />
              <p
                className="text-xs text-muted-foreground mt-1.5 text-left"
                data-testid="text-generation-phase"
              >
                {label}
              </p>
            </div>
          );
          return null;
        })()}

        {generatedDocument ? (
          <div>
            {generationProgressNode}
            <div className="flex items-center justify-between mb-2">
              {documentGeneratedAt && (
                <div className="flex flex-col gap-0.5">
                  <p className="text-xs text-muted-foreground" data-testid="text-document-generated-at">
                    Generated {format(new Date(documentGeneratedAt), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                  {selectedModel && (
                    <p className="text-xs text-muted-foreground/60" data-testid="text-model-used">
                      {selectedModel}
                    </p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3">
                {sourceMap && sourceMap.length > 0 && (transcription || notes) && (
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sourcesPanelOpen}
                      onChange={(e) => setSourcesPanelOpen(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-border"
                      data-testid="checkbox-show-sources"
                    />
                    <span className="text-xs text-muted-foreground">Sources</span>
                  </label>
                )}
                {documentSections.length > 1 && (
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showToc}
                      onChange={(e) => setShowToc(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-border"
                      data-testid="checkbox-show-toc"
                    />
                    <span className="text-xs text-muted-foreground">Sections</span>
                  </label>
                )}
                {documentSections.length > 0 && (() => {
                  const allPlain = documentSections.every(
                    (s) => (sectionViewModes[s.id] ?? defaultDocView) === "plain"
                  );
                  const nextMode: "markdown" | "plain" = allPlain ? "markdown" : "plain";
                  const label = allPlain ? "Show all as markdown" : "Show all as plain text";
                  return (
                    <button
                      type="button"
                      onClick={() =>
                        setAllSectionsViewMode(
                          documentSections.map((s) => s.id),
                          nextMode
                        )
                      }
                      className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-muted"
                      title={label}
                      aria-label={label}
                      data-testid="button-toggle-all-view"
                    >
                      {allPlain ? (
                        <Code className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : (
                        <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {allPlain ? "Markdown" : "Plain text"}
                      </span>
                    </button>
                  );
                })()}
              </div>
            </div>
            <div className="flex gap-4">
              {showToc && documentSections.length > 1 && (
                <div className="w-44 shrink-0">
                  <div className="sticky top-0 bg-muted/30 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      Sections
                    </h4>
                    <div className="space-y-0.5">
                      {documentSections.map((section, index) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToDocSection(section.id)}
                          className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors hover:bg-muted ${
                            activeTocSection === section.id
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-muted-foreground"
                          }`}
                          data-testid={`button-doc-toc-${section.id}`}
                        >
                          <span className="text-muted-foreground mr-1">{index + 1}.</span>
                          <span className="line-clamp-2">{section.title}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {sourcesPanelOpen && sourceMap && sourceMap.length > 0 && (transcription || notes) && (
                <div className="w-80 shrink-0">
                  <div className="sticky top-0 bg-muted/30 rounded-lg p-3 max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Sources
                      </h4>
                      {selectedTranscriptQuote && (
                        <button
                          onClick={() => setSelectedTranscriptQuote(null)}
                          className="text-[10px] text-muted-foreground hover:text-foreground underline"
                          data-testid="button-clear-source-selection"
                        >
                          clear
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-2">
                      Click a highlighted span in the transcript to focus it.
                    </p>
                    {transcription && (
                      <div className="mb-3" data-testid="panel-transcript-sources">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                          Transcript
                        </div>
                        {renderTranscriptWithHighlights(transcription)}
                      </div>
                    )}
                    {notes && (
                      <div data-testid="panel-notes-sources">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                          Notes
                        </div>
                        {renderTranscriptWithHighlights(notes)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div
                  ref={documentContainerRef}
                  onMouseUp={() => {
                    // Defer to next tick so the selection is finalized
                    setTimeout(() => {
                      const sel = window.getSelection();
                      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
                        setSelectionPopup(null);
                        return;
                      }
                      const text = sel.toString().trim();
                      if (!text) {
                        setSelectionPopup(null);
                        return;
                      }
                      const range = sel.getRangeAt(0);
                      const container = documentContainerRef.current;
                      if (!container) return;
                      const anchorNode =
                        range.commonAncestorContainer.nodeType === 3
                          ? range.commonAncestorContainer.parentElement
                          : (range.commonAncestorContainer as HTMLElement);
                      if (!anchorNode || !container.contains(anchorNode)) {
                        setSelectionPopup(null);
                        return;
                      }
                      const sectionEl =
                        anchorNode.closest?.("[data-section-id]") as
                          | HTMLElement
                          | null;
                      if (!sectionEl) {
                        setSelectionPopup(null);
                        return;
                      }
                      const sectionId = sectionEl.dataset.sectionId || "";
                      const sectionTitle = sectionEl.dataset.sectionTitle || "";
                      const rect = range.getBoundingClientRect();
                      if (rect.width === 0 && rect.height === 0) {
                        setSelectionPopup(null);
                        return;
                      }
                      setSelectionPopup({
                        x: rect.left + rect.width / 2,
                        y: rect.top - 8,
                        quote: text,
                        sectionId,
                        sectionTitle,
                      });
                    }, 0);
                  }}
                  className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 rounded-lg p-4"
                  data-testid="text-generated-document"
                >
                  {documentSections.map((section) => {
                    const stagedEntry = stagedBySectionId.get(section.id);
                    const hasPreview = !!stagedEntry?.row?.previewContent;
                    const baseContent = sectionContentMap.get(section.id) || '';
                    const content = hasPreview
                      ? (stagedEntry.row.previewContent as string)
                      : baseContent;
                    const sectionError = sectionErrorMap.get(section.id) || null;
                    const formatTemplate = staticSectionMap.get(section.id);
                    const stagedErrorsForSection = stagedSectionErrors[section.id] || {};
                    const stagedErrorCount = Object.keys(stagedErrorsForSection).length;
                    const learnIndicator = (
                      <div className="mb-2">
                        <LearnFromEditsIndicator
                          sessionId={sessionId}
                          sectionTitle={section.title}
                          userId={userId}
                        />
                      </div>
                    );
                    const stagedBanner = stagedEntry ? (
                      <div
                        className={`flex items-start gap-2 rounded-md border p-3 mb-3 text-sm ${hasPreview ? "border-amber-500/60 bg-amber-50/60 dark:bg-amber-950/30" : "border-amber-300/60 bg-amber-50/30 dark:bg-amber-950/15"}`}
                        data-testid={`staged-banner-${section.id}`}
                      >
                        <Sparkles className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                        <div className="flex-1">
                          <div className="font-semibold flex items-center gap-2">
                            {hasPreview ? "Previewing staged changes" : "Staged changes pending"}
                            <Badge variant="secondary" className="text-[10px]">
                              {stagedEntry.row.edits.length} staged
                            </Badge>
                          </div>
                          <div className="text-muted-foreground mt-0.5">
                            {hasPreview
                              ? "This section is showing the preview output. Apply to commit, or discard to revert."
                              : "Open the section assistant and run Preview regenerate to see the result."}
                          </div>
                          {stagedErrorCount > 0 && (
                            <div
                              className="mt-1 text-destructive flex items-center gap-1"
                              data-testid={`staged-banner-errors-${section.id}`}
                            >
                              <AlertCircle className="w-3 h-3" />
                              {stagedErrorCount} item{stagedErrorCount === 1 ? "" : "s"} failed — open section assistant to review.
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => void applyStagedForSection(section.id)}
                            disabled={!hasPreview || applyingStagedSectionId === section.id}
                            data-testid={`button-apply-staged-banner-${section.id}`}
                          >
                            {applyingStagedSectionId === section.id ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Check className="w-3 h-3 mr-1" />
                            )}
                            Apply staged
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void discardStagedForSection(section.id)}
                            disabled={applyingStagedSectionId === section.id}
                            data-testid={`button-discard-staged-banner-${section.id}`}
                          >
                            Discard
                          </Button>
                        </div>
                      </div>
                    ) : null;

                    const errorBanner = sectionError ? (
                      <div
                        className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 mb-3 text-sm"
                        data-testid={`alert-section-error-${section.id}`}
                      >
                        <AlertCircle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
                        <div className="flex-1">
                          <div className="font-semibold text-destructive" data-testid={`text-section-error-label-${section.id}`}>
                            {sectionErrorMeta(sectionError.type).label}
                          </div>
                          <div className="text-foreground/80 mt-0.5">
                            {sectionErrorMeta(sectionError.type).description}
                          </div>
                          {sectionError.message && (
                            <div className="text-muted-foreground mt-1 italic" data-testid={`text-section-error-message-${section.id}`}>
                              Details: {sectionError.message}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null;

                    const errorWrapperClass = sectionError
                      ? "border-2 border-destructive rounded-md p-3 mb-4 bg-destructive/5"
                      : "";

                    if (formatTemplate && !sectionError) {
                      return (
                        <div
                          key={section.id}
                          className="relative"
                          data-section-id={section.id}
                          data-section-title={section.title}
                          onMouseMove={(e) => handleSectionMouseMove(e, section)}
                          onMouseLeave={() => {
                            if (hoverSentence?.sectionId === section.id) setHoverSentence(null);
                          }}
                          onClick={(e) => handleSectionClick(e, section)}
                        >
                        {hoverSentence?.sectionId === section.id &&
                          hoverSentence.rects.map((r, idx) => (
                            <div
                              key={`hover-${idx}`}
                              className="pointer-events-none absolute bg-yellow-200/70 dark:bg-yellow-300/30 rounded-sm"
                              style={{ top: r.top, left: r.left, width: r.width, height: r.height }}
                              data-testid="sentence-hover-highlight"
                              data-section-id={section.id}
                            />
                          ))}
                        {learnIndicator}
                        {stagedBanner}
                        <EditableStaticSection
                          formatTemplate={formatTemplate}
                          renderedContent={content}
                          onContentChange={(newContent) => handleStaticSectionEdit(section.title, section.id, newContent)}
                          sectionTitle={section.title}
                          sectionId={section.id}
                          sectionRef={(el) => {
                            if (el) {
                              sectionRefs.current.set(section.id, el);
                            } else {
                              sectionRefs.current.delete(section.id);
                            }
                          }}
                          onRegenerate={handleRegenerateSection}
                          onCopy={copySectionContent}
                          onFocus={openFocusModal}
                          onLearn={openLearnModal}
                          onAssist={(title, id) => { void openSectionAssistant(title, id); }}
                          onHistory={(title) => setHistorySectionTitle(title)}
                          showHistoryButton={hasSectionHistory(section.title)}
                          regeneratingSectionId={regeneratingSectionId}
                          copiedSectionId={copiedSectionId}
                          regenerationStage={sectionProgress.get(section.id)}
                          viewMode={sectionViewModes[section.id] ?? defaultDocView}
                          onToggleViewMode={() => {
                            const stage = sectionProgress.get(section.id);
                            const stageActive = !!stage && stage !== "done" && stage !== "error" && stage !== "idle";
                            const locked = regeneratingSectionId === section.id || stageActive;
                            toggleSectionViewMode(section.id, locked);
                          }}
                        />
                        </div>
                      );
                    }

                    return (
                      <div
                        key={section.id}
                        className={`${errorWrapperClass} relative`.trim()}
                        data-section-id={section.id}
                        data-section-title={section.title}
                        data-testid={sectionError ? `section-error-${section.id}` : undefined}
                        onMouseMove={(e) => handleSectionMouseMove(e, section)}
                        onMouseLeave={() => {
                          if (hoverSentence?.sectionId === section.id) setHoverSentence(null);
                        }}
                        onClick={(e) => handleSectionClick(e, section)}
                      >
                        {hoverSentence?.sectionId === section.id &&
                          hoverSentence.rects.map((r, idx) => (
                            <div
                              key={`hover-${idx}`}
                              className="pointer-events-none absolute bg-yellow-200/70 dark:bg-yellow-300/30 rounded-sm"
                              style={{ top: r.top, left: r.left, width: r.width, height: r.height }}
                              data-testid="sentence-hover-highlight"
                              data-section-id={section.id}
                            />
                          ))}
                        {learnIndicator}
                        {stagedBanner}
                        {errorBanner}
                        <div className="section-content">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children, ...props }) => (
                              <p {...props}>{children}</p>
                            ),
                            li: ({ children, ...props }) => (
                              <li {...props}>{children}</li>
                            ),
                            h2: ({ children, ...props }) => {
                              const text = typeof children === "string" ? children : 
                                Array.isArray(children) ? children.map(c => typeof c === "string" ? c : "").join("") : "";
                              const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                              return (
                                <h2
                                  {...props}
                                  id={id}
                                  ref={(el) => {
                                    if (el) {
                                      sectionRefs.current.set(id, el);
                                    } else {
                                      sectionRefs.current.delete(id);
                                    }
                                  }}
                                  className="flex items-center justify-between group"
                                >
                                  <span>{children}</span>
                                  <span className="flex items-center gap-1 shrink-0 ml-2">
                                    <button
                                      onClick={() => {
                                        const stage = sectionProgress.get(id);
                                        const stageActive = !!stage && stage !== "done" && stage !== "error" && stage !== "idle";
                                        const locked = editingSectionId === id || regeneratingSectionId === id || stageActive;
                                        toggleSectionViewMode(id, locked);
                                      }}
                                      className="p-1 rounded hover:bg-muted"
                                      title={(sectionViewModes[id] ?? defaultDocView) === "plain" ? "Show as markdown" : "Show as plain text"}
                                      aria-label={(sectionViewModes[id] ?? defaultDocView) === "plain" ? "Show as markdown" : "Show as plain text"}
                                      data-testid={`button-toggle-view-${id}`}
                                    >
                                      {(sectionViewModes[id] ?? defaultDocView) === "plain" ? (
                                        <Code className="w-4 h-4 text-muted-foreground" />
                                      ) : (
                                        <FileText className="w-4 h-4 text-muted-foreground" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => { void openSectionAssistant(text, id); }}
                                      className="p-1 rounded hover:bg-muted"
                                      title="Ask AI about this section"
                                      data-testid={`button-ai-section-${id}`}
                                    >
                                      <Sparkles className="w-4 h-4 text-primary" />
                                    </button>
                                    <button
                                      onClick={() => openLearnModal(text, id)}
                                      className="p-1 rounded hover:bg-muted"
                                      title="Teach AI from this section"
                                      data-testid={`button-learn-section-${id}`}
                                    >
                                      <GraduationCap className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingSectionId(id);
                                        setEditingContent(sectionContentMap.get(id) || '');
                                      }}
                                      disabled={editingSectionId !== null}
                                      className="p-1 rounded hover:bg-muted disabled:opacity-50"
                                      title="Edit section"
                                      data-testid={`button-edit-section-${id}`}
                                    >
                                      <Pencil className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                    {hasSectionHistory(text) && (
                                      <button
                                        onClick={() => setHistorySectionTitle(text)}
                                        className="p-1 rounded hover:bg-muted"
                                        title="Section history"
                                        data-testid={`button-history-section-${id}`}
                                      >
                                        <History className="w-4 h-4 text-muted-foreground" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleRegenerateSection(text, id)}
                                      disabled={regeneratingSectionId !== null}
                                      className="p-1 rounded hover:bg-muted disabled:opacity-50"
                                      title="Regenerate section"
                                      data-testid={`button-regen-section-${id}`}
                                    >
                                      {regeneratingSectionId === id ? (
                                        <Loader2 className="w-4 h-4 text-primary animate-spin" />
                                      ) : (
                                        <RefreshCw className="w-4 h-4 text-muted-foreground" />
                                      )}
                                    </button>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          className="p-1 rounded hover:bg-muted"
                                          title="Copy section"
                                          data-testid={`button-copy-section-${id}`}
                                        >
                                          {copiedSectionId === id ? (
                                            <Check className="w-4 h-4 text-green-500" />
                                          ) : (
                                            <Copy className="w-4 h-4 text-muted-foreground" />
                                          )}
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={() => copySectionContent(id, "plain")}
                                          data-testid={`button-copy-section-plain-${id}`}
                                        >
                                          Copy as plain text
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => copySectionContent(id, "markdown")}
                                          data-testid={`button-copy-section-markdown-${id}`}
                                        >
                                          Copy with markdown
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </span>
                                </h2>
                              );
                            },
                          }}
                        >
                          {(() => {
                            const stage = sectionProgress.get(section.id);
                            const stageActive = stage && stage !== "done" && stage !== "error";
                            const isPlainView = (sectionViewModes[section.id] ?? defaultDocView) === "plain";
                            const hideContent = isPlainView || editingSectionId === section.id || regeneratingSectionId === section.id || stageActive;
                            return `## ${section.title}${hideContent ? '' : `\n\n${content}`}`;
                          })()}
                        </ReactMarkdown>
                        </div>
                        {(() => {
                          const stage = sectionProgress.get(section.id);
                          const stageActive = stage && stage !== "done" && stage !== "error";
                          const isPlainView = (sectionViewModes[section.id] ?? defaultDocView) === "plain";
                          if (!isPlainView) return null;
                          if (editingSectionId === section.id || regeneratingSectionId === section.id || stageActive) return null;
                          return (
                            <pre
                              className="whitespace-pre-wrap break-words font-sans text-sm text-foreground"
                              data-testid={`text-section-plain-${section.id}`}
                            >
                              {content}
                            </pre>
                          );
                        })()}
                        {(() => {
                          const stage = sectionProgress.get(section.id);
                          const stageActive = stage && stage !== "done" && stage !== "error";
                          if (regeneratingSectionId !== section.id && !stageActive) return null;
                          return (
                            <RegeneratingPlaceholder
                              testId={`placeholder-regenerating-${section.id}`}
                              stage={stage}
                            />
                          );
                        })()}
                        {editingSectionId === section.id && (
                          <div className="mt-2">
                            <Textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              className="min-h-[200px] font-mono text-sm"
                              data-testid={`textarea-edit-section-${section.id}`}
                            />
                            <div className="flex items-center gap-1 mt-2">
                              <button
                                onClick={async () => {
                                  await handleStaticSectionEdit(section.title, section.id, editingContent);
                                  setEditingSectionId(null);
                                  setEditingContent("");
                                }}
                                className="p-1.5 rounded hover:bg-muted text-green-600"
                                title="Save edit"
                                data-testid={`button-save-edit-${section.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingSectionId(null);
                                  setEditingContent("");
                                }}
                                className="p-1.5 rounded hover:bg-muted text-red-500"
                                title="Cancel edit"
                                data-testid={`button-cancel-edit-${section.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-muted/30 rounded-lg p-6 text-center">
            {generationProgressNode}
            <Sparkles className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              Click "Generate Document" to create a structured document from the transcription using the assigned template.
            </p>
          </div>
        )}
      </div>

      {sentenceMenu && (
        <div
          ref={sentenceMenuRef}
          style={{
            position: "fixed",
            left: sentenceMenu.x,
            top: sentenceMenu.y,
            transform: "translateX(-50%)",
            zIndex: 50,
          }}
          data-testid="menu-sentence-discuss"
        >
          <button
            type="button"
            onClick={() => {
              const { sectionTitle, sectionId, text } = sentenceMenu;
              setSentenceMenu(null);
              setHoverSentence(null);
              void openSentenceDiscussion(sectionTitle, sectionId, text);
            }}
            className="flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-2.5 py-1.5 text-xs font-medium shadow-md hover:opacity-90"
            data-testid="button-discuss-sentence"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Discuss with AI?
          </button>
        </div>
      )}

      {selectionPopup && (
        <div
          ref={selectionPopupRef}
          style={{
            position: "fixed",
            left: selectionPopup.x,
            top: selectionPopup.y,
            transform: "translate(-50%, -100%)",
            zIndex: 50,
          }}
          data-testid="popup-ask-assistant-selection"
        >
          <button
            type="button"
            onMouseDown={(e) => {
              // Prevent the mousedown from collapsing the selection before click fires
              e.preventDefault();
            }}
            onClick={() => {
              const { quote, sectionId, sectionTitle } = selectionPopup;
              void openSectionAssistant(sectionTitle, sectionId, quote);
              setSelectionPopup(null);
              const sel = window.getSelection();
              sel?.removeAllRanges();
            }}
            className="flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-2.5 py-1.5 text-xs font-medium shadow-md hover:opacity-90"
            data-testid="button-ask-assistant-selection"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Ask assistant about this
          </button>
        </div>
      )}

      <Dialog open={!!learnModalSection} onOpenChange={(open) => { if (!open) { setLearnModalSection(null); setImprovementText(""); setLearnMode("choose"); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Teach AI: {learnModalSection?.title}
            </DialogTitle>
            <DialogDescription>
              {learnMode === "choose"
                ? "Help the AI improve future generations of this section."
                : "Describe what should be different. Your correction will be added to this section's instructions so future generations follow your guidance."}
            </DialogDescription>
          </DialogHeader>

          {learnMode === "choose" ? (
            <div className="flex flex-col gap-2 mt-2">
              <Button
                onClick={handleLooksGood}
                disabled={isSavingFeedback}
                className="justify-start"
                data-testid="button-learn-looks-good"
              >
                {isSavingFeedback ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ThumbsUp className="w-4 h-4 mr-2" />
                )}
                Looks Good — save as example
              </Button>
              <Button
                variant="outline"
                onClick={() => setLearnMode("improve")}
                disabled={isSavingFeedback}
                className="justify-start"
                data-testid="button-learn-needs-improvement"
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                Needs Improvement
              </Button>
            </div>
          ) : (
            <>
              <Textarea
                placeholder={`e.g. "Always include the patient's reported pain level on a 0-10 scale."`}
                value={improvementText}
                onChange={(e) => setImprovementText(e.target.value)}
                className="min-h-[120px]"
                data-testid="textarea-improvement"
              />
              <div className="flex justify-between gap-2 mt-2">
                <Button
                  variant="ghost"
                  onClick={() => setLearnMode("choose")}
                  disabled={isSavingFeedback}
                  data-testid="button-learn-back"
                >
                  Back
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setLearnModalSection(null); setImprovementText(""); setLearnMode("choose"); }}
                    disabled={isSavingFeedback}
                    data-testid="button-learn-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitImprovement}
                    disabled={!improvementText.trim() || isSavingFeedback}
                    data-testid="button-learn-submit"
                  >
                    {isSavingFeedback ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      "Save Correction"
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!focusModalSection} onOpenChange={(open) => { if (!open) setFocusModalSection(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Focus Instruction</DialogTitle>
            <DialogDescription>
              Provide clarification or areas of focus for the AI when regenerating the <strong>{focusModalSection?.title}</strong> section. This will guide the model to emphasize points it may have missed.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder='e.g. "Focus on the fact that the patient still has depression."'
            value={focusText}
            onChange={(e) => setFocusText(e.target.value)}
            className="min-h-[100px]"
            data-testid="textarea-focus-instruction"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setFocusModalSection(null)}
              data-testid="button-focus-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleFocusRegenerate}
              disabled={!focusText.trim()}
              data-testid="button-focus-submit"
            >
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={compareOpen} onOpenChange={(open) => { if (!open) { setCompareOpen(false); setSelectedCompareSession(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-4 h-4" />
              Compare with Another Session
            </DialogTitle>
            <DialogDescription>
              Select a previous recording session to compare with the current one. The AI will identify only the differences.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sessions by name or patient..."
                value={compareSearch}
                onChange={(e) => setCompareSearch(e.target.value)}
                className="pl-9"
                data-testid="input-compare-search"
              />
            </div>
            <ScrollArea className="h-64 rounded-md border border-border">
              {filteredCompareSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-8 text-center px-4">
                  <p className="text-sm text-muted-foreground">
                    {otherSessions.length === 0
                      ? "No other recording sessions found."
                      : "No sessions match your search."}
                  </p>
                </div>
              ) : (
                <div className="p-1">
                  {filteredCompareSessions.map((session) => (
                    <button
                      key={session._id}
                      onClick={() => setSelectedCompareSession(session)}
                      className={`w-full text-left px-3 py-2.5 rounded-md transition-colors mb-0.5 ${
                        selectedCompareSession?._id === session._id
                          ? "bg-primary/10 border border-primary/30"
                          : "hover:bg-muted"
                      }`}
                      data-testid={`button-compare-session-${session._id}`}
                    >
                      <p className="text-sm font-medium text-foreground line-clamp-1">{session.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {session.patientName} &middot; {format(new Date(session.recordingDate), "MMM d, yyyy")}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => { setCompareOpen(false); setSelectedCompareSession(null); }}
              data-testid="button-compare-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompare}
              disabled={!selectedCompareSession}
              data-testid="button-compare-select"
            >
              <GitCompare className="w-3 h-3 mr-1" />
              Select
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={compareResultOpen} onOpenChange={(open) => { if (!open) setCompareResultOpen(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-4 h-4" />
              Session Differences
            </DialogTitle>
            <DialogDescription>
              Comparing <strong>&ldquo;{sessionTitle}&rdquo;</strong> with <strong>&ldquo;{selectedCompareSession?.title}&rdquo;</strong>. Only differences are shown below.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 mt-2 pr-2">
            <div className="prose prose-sm dark:prose-invert max-w-none" data-testid="text-compare-result">
              {compareResult && (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {compareResult}
                </ReactMarkdown>
              )}
            </div>
          </ScrollArea>
          <div className="flex justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setCompareResultOpen(false)}
              data-testid="button-compare-result-close"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {historySectionTitle && (
        <DocumentSectionHistoryPanel
          open={!!historySectionTitle}
          onOpenChange={(open) => { if (!open) setHistorySectionTitle(null); }}
          userId={userId}
          sessionId={sessionId}
          sectionTitle={historySectionTitle}
          currentContent={(() => {
            const id = historySectionTitle
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            return sectionContentMap.get(id) || "";
          })()}
        />
      )}
    </div>
  );
}

const URL_TAB_TO_INTERNAL: Record<string, string> = {
  document: "document",
  transcription: "transcript",
  note: "notes",
  "transcription-experimental": "transcript-experimental",
  speakers: "speakers",
};
const INTERNAL_TAB_TO_URL: Record<string, string> = {
  document: "document",
  transcript: "transcription",
  notes: "note",
  "transcript-experimental": "transcription-experimental",
  speakers: "speakers",
};

class SessionPaneErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Session pane render error:", error, info);
  }
  reset = () => this.setState({ hasError: false });
  render() {
    if (this.state.hasError) {
      return (
        <div
          className="h-full flex flex-col items-center justify-center text-center px-6"
          data-testid="state-session-render-error"
        >
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground mb-2">
            Something went wrong displaying this session
          </h2>
          <p className="text-muted-foreground max-w-md mb-6">
            We hit an unexpected error while rendering this session. You can try again or pick a different session from the sidebar.
          </p>
          <Button onClick={this.reset} data-testid="button-retry-session-render">
            Retry
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Dashboard() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const [matchedTab, paramsTab] = useRoute<{ sessionId: string; tab: string }>(
    "/dashboard/session/:sessionId/:tab"
  );
  const [matchedSession, paramsSession] = useRoute<{ sessionId: string }>(
    "/dashboard/session/:sessionId"
  );
  const routeSessionId = (matchedTab
    ? paramsTab?.sessionId
    : matchedSession
      ? paramsSession?.sessionId
      : null) as Id<"recordingSessions"> | null;
  const rawUrlTab = matchedTab ? paramsTab?.tab : undefined;
  const routeTab = rawUrlTab && URL_TAB_TO_INTERNAL[rawUrlTab]
    ? URL_TAB_TO_INTERNAL[rawUrlTab]
    : null;
  const selectedSessionId = routeSessionId;

  const [sideChatOpen, setSideChatOpen] = useState(false);
  const [userSynced, setUserSynced] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [deleteSessionId, setDeleteSessionId] = useState<Id<"recordingSessions"> | null>(null);
  const [, setHasGeneratedDocument] = useState(false);
  const [renamingSessionId, setRenamingSessionId] = useState<Id<"recordingSessions"> | null>(null);
  const [sessionSearch, setSessionSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [editingCenterTitle, setEditingCenterTitle] = useState(false);
  const [centerTitleValue, setCenterTitleValue] = useState("");
  const [autoGenerateRequested, setAutoGenerateRequested] = useState(false);
  const [transcriptCopied, setTranscriptCopied] = useState(false);
  const [isAttributing, setIsAttributing] = useState(false);
  const [transcriptInitialMode, setTranscriptInitialMode] = useState<"raw" | "labeled">("raw");
  const { toast } = useToast();

  // Latest-pending pattern: if a trigger arrives while one is in flight, we
  // remember the session ID and fire one more time after the current call
  // returns, so the final saved content is always processed.
  const pendingAttributionRef = useRef<string | null>(null);
  const triggerSpeakerAttribution = async (sessionId: string) => {
    if (isAttributing) {
      // Remember the latest session ID to re-fire after the current run.
      pendingAttributionRef.current = sessionId;
      return;
    }
    setIsAttributing(true);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/speaker-attribution`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        let message = "Speaker labeling failed. Please try again.";
        try {
          const body = await res.json();
          if (body?.error) message = body.error;
        } catch {}
        toast({ title: "Speaker labeling failed", description: message, variant: "destructive" });
      }
    } catch (err) {
      console.error("[speakerAttribution] Trigger failed:", err);
      toast({ title: "Speaker labeling failed", description: "Could not reach the server. Please try again.", variant: "destructive" });
    } finally {
      setIsAttributing(false);
      // If a new trigger arrived while we were busy, fire it now.
      const pending = pendingAttributionRef.current;
      if (pending) {
        pendingAttributionRef.current = null;
        triggerSpeakerAttribution(pending);
      }
    }
  };

  const handleCopyTranscript = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setTranscriptCopied(true);
      toast({ title: "Transcript copied", description: "The full transcript is on your clipboard." });
      setTimeout(() => setTranscriptCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy transcript:", err);
      toast({
        title: "Copy failed",
        description: "We couldn't access your clipboard. Please try again.",
        variant: "destructive",
      });
    }
  };
  const { isRecordingActive } = useRecordingStatus();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(sessionSearch.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [sessionSearch]);

  const goToSession = (
    id: Id<"recordingSessions"> | null,
    tab?: string | null,
  ) => {
    if (!id) {
      navigate("/dashboard");
      return;
    }
    const internal = tab ?? routeTab;
    if (internal && INTERNAL_TAB_TO_URL[internal]) {
      navigate(`/dashboard/session/${id}/${INTERNAL_TAB_TO_URL[internal]}`);
    } else {
      navigate(`/dashboard/session/${id}`);
    }
  };
  const setSelectedSessionId = (
    id: Id<"recordingSessions"> | null,
    opts?: { hasDocument?: boolean; tab?: "document" | "transcript" | "notes" },
  ) => {
    if (!id) {
      navigate("/dashboard");
      return;
    }
    const internal =
      opts?.tab ?? (opts?.hasDocument ? "document" : "transcript");
    navigate(`/dashboard/session/${id}/${INTERNAL_TAB_TO_URL[internal]}`);
  };
  
  const getOrCreateUser = useMutation(api.users.getOrCreateUser);
  const deleteSession = useMutation(api.recordingSessions.remove);
  const updateSessionMutation = useMutation(api.recordingSessions.update);
  const initialSyncDone = useRef(false);
  const syncRetryCount = useRef(0);
  const MAX_SYNC_RETRIES = 3;

  const [convexUserId, setConvexUserId] = useState<Id<"users"> | null>(null);

  const selectedSession = useQuery(
    api.recordingSessions.get,
    selectedSessionId && convexUserId
      ? { id: selectedSessionId, userId: convexUserId }
      : "skip"
  );

  // activeTab is derived from the URL; when the URL omits the tab we fall back
  // to "document" if the session already has a generated document, otherwise
  // "transcript". This preserves the previous default-tab behavior.
  const activeTab: string = routeTab
    ?? (selectedSession?.generatedDocument ? "document" : "transcript");

  const setActiveTab = (next: string) => {
    if (!selectedSessionId) return;
    goToSession(selectedSessionId, next);
  };

  const prevSessionIdRef = useRef(selectedSessionId);

  useEffect(() => {
    if (selectedSession?.status === "failed" && autoGenerateRequested) {
      setAutoGenerateRequested(false);
    }
  }, [selectedSession?.status, autoGenerateRequested]);

  useEffect(() => {
    if (prevSessionIdRef.current !== selectedSessionId) {
      prevSessionIdRef.current = selectedSessionId;
      setAutoGenerateRequested(false);
      setTranscriptInitialMode("raw");
    }
  }, [selectedSessionId]);

  // If the URL points to a session that doesn't exist or the user doesn't own,
  // show an in-pane "not found / no access" state and auto-redirect back to
  // /dashboard after a short delay. `selectedSession === null` means the
  // query resolved with no result; `undefined` means still loading.
  const sessionUnavailable =
    !!selectedSessionId && !!convexUserId && selectedSession === null;

  useEffect(() => {
    if (!sessionUnavailable) return;
    const t = window.setTimeout(() => {
      navigate("/dashboard");
    }, 2500);
    return () => window.clearTimeout(t);
  }, [sessionUnavailable, navigate]);

  // Auto-trigger speaker attribution whenever a session's transcript or notes change.
  // Fired unconditionally — server-side content hash memoization and the per-session
  // lock prevent duplicate LLM work when content hasn't actually changed.
  // Covers: initial transcription, re-transcription, and notes edits that may change
  // speaker context. The manual "Label Speakers" button remains for user-initiated runs.
  useEffect(() => {
    if (selectedSession && (selectedSession.transcription || selectedSession.notes)) {
      triggerSpeakerAttribution(selectedSession._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?._id, selectedSession?.transcription, selectedSession?.notes]);

  useEffect(() => {
    if (user && !userSynced) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      }).then(() => {
        setUserSynced(true);
        setSyncError(null);
        initialSyncDone.current = true;
      }).catch((err) => {
        console.error("Failed to sync user with Convex:", err);
        if (syncRetryCount.current < MAX_SYNC_RETRIES) {
          syncRetryCount.current += 1;
          setTimeout(() => {
            setUserSynced(false);
          }, 1500 * syncRetryCount.current);
        } else {
          setSyncError(err?.message || "Failed to sync user data. Please try again.");
          setUserSynced(true);
          initialSyncDone.current = true;
        }
      });
    }
  }, [user, userSynced, getOrCreateUser]);

  useEffect(() => {
    if (activeTab === "transcript" && user && initialSyncDone.current) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      }).then(() => {
        setUserSynced(true);
        setSyncError(null);
      }).catch((err) => {
        console.error("Failed to sync user with Convex on tab switch:", err);
      });
    }
  }, [activeTab, user, getOrCreateUser]);
  
  const convexUser = useQuery(
    api.users.getCurrentUser, 
    user && userSynced ? { googleId: user.id, email: user.email || undefined } : "skip"
  );

  const userSettings = useQuery(
    api.userSettings.get,
    convexUser && user ? { userId: convexUser._id, googleId: user.id } : "skip",
  );
  const defaultDocView: "markdown" | "plain" =
    userSettings?.defaultDocumentView ?? "markdown";

  useEffect(() => {
    if (convexUser?._id && convexUser._id !== convexUserId) {
      setConvexUserId(convexUser._id);
    }
  }, [convexUser, convexUserId]);

  const acceptPendingEmailInvites = useMutation(api.teams.acceptPendingEmailInvites);
  const acceptedInvitesRef = useRef(false);
  useEffect(() => {
    if (!convexUser?._id || acceptedInvitesRef.current) return;
    acceptedInvitesRef.current = true;
    acceptPendingEmailInvites({ userId: convexUser._id })
      .then((res) => {
        if (res?.joined) {
          toast({
            title: "You've joined a team",
            description: res.teamName
              ? `Welcome to ${res.teamName}.`
              : "An invitation was accepted.",
          });
        }
      })
      .catch(() => {});
  }, [convexUser, acceptPendingEmailInvites]);

  useEffect(() => {
    if (convexUser === null && userSynced && !syncError && user) {
      if (syncRetryCount.current < MAX_SYNC_RETRIES) {
        syncRetryCount.current += 1;
        const delay = 1500 * syncRetryCount.current;
        const timer = setTimeout(() => {
          setUserSynced(false);
        }, delay);
        return () => clearTimeout(timer);
      }
    }
  }, [convexUser, userSynced, syncError, user]);
  
  const isAdmin = useIsAdmin();
  const isExperimental = useIsExperimental();

  const sessions = useQuery(
    api.recordingSessions.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );

  const searchResults = useQuery(
    api.recordingSessions.search,
    convexUser && debouncedSearch
      ? { userId: convexUser._id, query: debouncedSearch }
      : "skip"
  );

  const templates = useQuery(
    api.templates.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );

  const templateMap = new Map(
    (templates || []).map((t) => [t._id, t.name])
  );

  const sessionTemplate = useQuery(
    api.templates.get,
    selectedSession?.templateId && convexUser
      ? { id: selectedSession.templateId, userId: convexUser._id }
      : "skip"
  );

  const experimentalTemplateSections = useQuery(
    api.templates.getSections,
    selectedSession?.templateId && convexUser
      ? { templateId: selectedSession.templateId, userId: convexUser._id }
      : "skip"
  );

  const resumedSessionsRef = useRef<Set<string>>(new Set());
  const isResumingRef = useRef(false);
  const [, setIsResuming] = useState(false);

  useEffect(() => {
    if (!sessions || !convexUser) return;
    if (isResumingRef.current) return;

    const resumeIncomplete = async () => {
      const needsCompletion = sessions.filter(
        (s) =>
          s.audioApiRecordingId &&
          !s.audioUrl &&
          !s.transcription &&
          s.status !== "completed" &&
          s.status !== "failed" &&
          s.status !== "recording" &&
          Date.now() - s._creationTime > 60_000 &&
          !resumedSessionsRef.current.has(s._id)
      );

      const needsTranscription = sessions.filter(
        (s) =>
          (s.audioApiRecordingId || s.audioUrl) &&
          !s.transcription &&
          s.status === "processing" &&
          Date.now() - s._creationTime > 60_000 &&
          !resumedSessionsRef.current.has(s._id)
      );

      if (needsCompletion.length === 0 && needsTranscription.length === 0) return;

      isResumingRef.current = true;
      setIsResuming(true);

      try {
        const resumeSessionTranscript = async (
          sess: typeof sessions[0],
          needsComplete: boolean,
        ) => {
          const recordingId = sess.audioApiRecordingId!;
          const gatewaySessionId = (sess as any).gatewaySessionId as string | undefined;

          if (!gatewaySessionId) {
            toast({
              title: "Cannot resume this recording",
              description: `"${sess.title}" was recorded before the current transcription system and cannot be automatically transcribed.`,
              variant: "destructive",
            });
            await updateSessionMutation({ id: sess._id, userId: convexUser._id, status: "failed" });
            return;
          }

          if (needsComplete) {
            toast({ title: "Resuming recording", description: `Finishing upload for "${sess.title}"...` });
            const statusData = await audioApiGetStatus(recordingId);
            const totalChunks = statusData.totalChunks || statusData.chunksReceived;
            if (!totalChunks || totalChunks === 0) {
              throw new Error("No audio chunks found for this recording");
            }
            const completeData = await audioApiComplete(recordingId, totalChunks);
            const audioUrl = completeData.audioUrl || completeData.url || completeData.s3Url;
            await updateSessionMutation({ id: sess._id, userId: convexUser._id, audioUrl });
          }

          toast({ title: "Transcribing audio", description: `Starting transcription for "${sess.title}"...` });
          try {
            await updateSessionMutation({ id: sess._id, userId: convexUser._id, status: "processing" });
          } catch {}

          // Check whether transcription already completed on the gateway
          let resumeStatusData: any;
          try {
            resumeStatusData = await audioApiGetStatus(recordingId);
          } catch (err) {
            console.warn("[Auto-resume] Status fetch failed:", err);
          }
          const resumeRecStatus: string = resumeStatusData?.status ?? "";
          const alreadyComplete = resumeRecStatus === "transcription_complete" || resumeRecStatus === "completed";

          if (!alreadyComplete) {
            // Re-complete so the gateway (re)runs transcription
            const totalChunks = resumeStatusData?.totalChunks || resumeStatusData?.chunksReceived || 0;
            if (totalChunks > 0) {
              await audioApiComplete(recordingId, totalChunks);
            }
          }

          // Poll until transcription complete or failed
          let transcript: string | null = null;
          const maxAttempts = 60;
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise<void>((r) => setTimeout(r, 5000));
            let latestStatus: any;
            try {
              latestStatus = await audioApiGetStatus(recordingId);
            } catch { continue; }
            const latestRecStatus: string = latestStatus?.status ?? "";
            if (latestRecStatus === "transcription_failed" || latestRecStatus === "failed") {
              throw new Error("transcription_failed");
            }
            if (latestRecStatus === "transcription_complete" || latestRecStatus === "completed") {
              try {
                const transcriptData = await audioApiGetSessionTranscript(gatewaySessionId);
                if (transcriptData !== null && transcriptData?.transcript) {
                  transcript = transcriptData.transcript;
                  break;
                }
              } catch { continue; }
            }
          }

          if (!transcript) throw new Error("Transcription timed out");

          await updateSessionMutation({
            id: sess._id,
            userId: convexUser._id,
            transcription: transcript,
            status: "completed",
          });

          toast({ title: "Transcription complete", description: `"${sess.title}" has been transcribed successfully.` });
        };

        for (const session of needsCompletion) {
          resumedSessionsRef.current.add(session._id);
          try {
            await resumeSessionTranscript(session, true);
          } catch (error) {
            console.error("Auto-resume error for session:", session._id, error);
            try {
              await updateSessionMutation({ id: session._id, userId: convexUser._id, status: "failed" });
            } catch {}
            toast({
              title: "Resume failed",
              description: `Could not complete "${session.title}". You can retry from the session.`,
              variant: "destructive",
            });
          }
        }

        for (const session of needsTranscription) {
          resumedSessionsRef.current.add(session._id);
          try {
            await resumeSessionTranscript(session, false);
          } catch (error) {
            console.error("Auto-resume transcription error:", session._id, error);
            try {
              await updateSessionMutation({ id: session._id, userId: convexUser._id, status: "failed" });
            } catch {}
            toast({
              title: "Transcription failed",
              description: `Could not transcribe "${session.title}". You can retry from the session.`,
              variant: "destructive",
            });
          }
        }

      } catch (error) {
        console.error("Auto-resume unexpected error:", error);
      } finally {
        isResumingRef.current = false;
        setIsResuming(false);
      }
    };

    if (!isRecordingActive) {
      resumeIncomplete();
    }
  }, [sessions, convexUser, toast, updateSessionMutation, isRecordingActive]);

  const handleDeleteSession = async () => {
    if (!deleteSessionId || !convexUser) return;
    try {
      await deleteSession({ id: deleteSessionId, userId: convexUser._id });
      toast({
        title: "Session deleted",
        description: "The recording session has been permanently deleted.",
      });
      if (selectedSessionId === deleteSessionId) {
        setSelectedSessionId(null);
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete the session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteSessionId(null);
    }
  };

  const handleRenameSession = async (sessionId: Id<"recordingSessions">, newTitle: string) => {
    if (!convexUser || !newTitle.trim()) return;
    try {
      await updateSessionMutation({
        id: sessionId,
        userId: convexUser._id,
        title: newTitle.trim(),
      });
      toast({ title: "Session renamed" });
    } catch {
      toast({ title: "Error", description: "Failed to rename session.", variant: "destructive" });
    }
    setRenamingSessionId(null);
    setEditingCenterTitle(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!userSynced) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (syncError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="sync-error-container">
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold text-foreground">Unable to load your dashboard</h2>
          <p className="text-muted-foreground">{syncError}</p>
          <Button
            data-testid="button-retry-sync"
            onClick={() => {
              syncRetryCount.current = 0;
              setSyncError(null);
              setUserSynced(false);
              initialSyncDone.current = false;
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (convexUser === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (convexUser === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="sync-error-container">
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold text-foreground">Unable to load your dashboard</h2>
          <p className="text-muted-foreground">We couldn't retrieve your account data. This may be a temporary issue.</p>
          <Button
            data-testid="button-retry-sync"
            onClick={() => {
              syncRetryCount.current = 0;
              setSyncError(null);
              setUserSynced(false);
              initialSyncDone.current = false;
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Dashboard — LRScribe" noindex />
      {/* Top Header */}
      <header className="relative h-[26px] border-b border-border bg-card flex items-center justify-between px-2 shrink-0 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href="/dashboard"
            className="flex items-center gap-1 shrink-0"
            aria-label="LRScribe home"
          >
            <div className="p-0.5 bg-primary/10 rounded-sm">
              <Stethoscope className="w-3 h-3 text-primary" />
            </div>
            <span className="font-heading font-bold text-xs text-foreground hidden sm:inline leading-none">
              LRScribe
            </span>
          </Link>
        </div>

        <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Link
            href="/dashboard"
            className="text-xs font-medium text-primary transition-colors flex items-center gap-1 leading-none"
            data-testid="link-dashboard"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            href="/templates"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-templates"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Templates</span>
          </Link>
          <Link
            href="/rules"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-rules"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Rules</span>
          </Link>
          <Link
            href="/profiles"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-profiles"
          >
            <UserRound className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Profiles</span>
          </Link>
          <Link
            href="/chat"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </Link>
          <Link
            href="/team"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-team"
          >
            <Users className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Team</span>
          </Link>
        </nav>

        <div className="flex items-center gap-0.5 shrink-0">
          <ThemeToggle className="h-5 w-5 [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background cursor-pointer"
                data-testid="button-user-menu"
              >
                <Avatar className="h-5 w-5 pointer-events-none">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div
                  className="text-sm font-medium text-foreground truncate"
                  data-testid="text-user-name"
                >
                  {user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4 mr-2" />
                  Change log
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings" data-testid="link-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Pane - Session List */}
        <aside className={`w-full md:w-80 border-r border-border bg-card flex flex-col shrink-0 ${selectedSessionId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-border flex flex-col gap-3">
            {convexUser && (
              <NewSessionDialog 
                userId={convexUser._id} 
                onCreated={(id) => setSelectedSessionId(id, { tab: "transcript" })}
                disabled={isRecordingActive}
              />
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={sessionSearch}
                onChange={(e) => setSessionSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="input-search-sessions"
              />
            </div>
          </div>
          
          {convexUser && (
            <SharedWithMeList
              userId={convexUser._id}
              selectedSessionId={selectedSessionId}
              onSelect={(id) => setSelectedSessionId(id, { tab: "transcript" })}
            />
          )}

          <ScrollArea className="flex-1">
            <div className="p-2">
              {(() => {
                const isSearching = debouncedSearch.length > 0;
                const isLoadingSearch = isSearching && searchResults === undefined;
                const activeSessions = isSearching ? (searchResults ?? []) : sessions;

                if (!isSearching && sessions === undefined) {
                  return (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  );
                }

                if (isLoadingSearch) {
                  return (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  );
                }

                if (!isSearching && sessions !== undefined && sessions.length === 0) {
                  return (
                    <div className="text-center py-12 px-4">
                      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                        <Mic className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        No recording sessions yet. Create your first session to get started.
                      </p>
                    </div>
                  );
                }

                const filtered = activeSessions ?? [];
                return filtered.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <p className="text-sm text-muted-foreground">No sessions match your search.</p>
                  </div>
                ) : (
                <div className="space-y-1">
                  {filtered.map((session) => (
                    <div
                      key={session._id}
                      className={`w-full text-left p-3 rounded-lg transition-colors group relative ${
                        selectedSessionId === session._id 
                          ? "bg-primary/10 border border-primary/20" 
                          : "hover:bg-muted/50"
                      }`}
                    >
                      {renamingSessionId === session._id ? (
                        <div className="pr-8" onClick={(e) => e.stopPropagation()}>
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenameSession(session._id, renameValue);
                              if (e.key === "Escape") setRenamingSessionId(null);
                            }}
                            onBlur={() => handleRenameSession(session._id, renameValue)}
                            className="w-full text-sm font-medium bg-background border border-primary/30 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-primary"
                            data-testid={`input-rename-session-${session._id}`}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            if (isRecordingActive && selectedSessionId !== session._id) {
                              toast({
                                title: "Recording in progress",
                                description: "Please stop the current recording before switching sessions.",
                                variant: "destructive",
                              });
                              return;
                            }
                            setSelectedSessionId(session._id, {
                              hasDocument: !!session.generatedDocument,
                            });
                            setHasGeneratedDocument(false);
                            setEditingCenterTitle(false);
                          }}
                          className={`w-full text-left pr-8 ${isRecordingActive && selectedSessionId !== session._id ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid={`button-session-${session._id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <h3 className={`font-medium text-sm truncate ${
                                selectedSessionId === session._id ? "text-primary" : "text-foreground"
                              }`}>
                                {session.patientName}
                              </h3>
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {session.templateId && templateMap.has(session.templateId) 
                                  ? templateMap.get(session.templateId) 
                                  : session.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(session.recordingDate), "MMM d, yyyy")}
                                {session.duration != null && session.duration > 0 && (
                                  <>
                                    <span className="mx-0.5">·</span>
                                    <Clock className="w-3 h-3" />
                                    {session.duration >= 3600 
                                      ? `${Math.floor(session.duration / 3600)}h ${Math.floor((session.duration % 3600) / 60)}m`
                                      : session.duration >= 60
                                        ? `${Math.floor(session.duration / 60)}m ${Math.floor(session.duration % 60)}s`
                                        : `${Math.floor(session.duration)}s`}
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <StatusBadge 
                                status={session.status} 
                                hasTranscription={!!session.transcription}
                                hasGeneratedDocument={!!session.generatedDocument}
                              />
                              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${
                                selectedSessionId === session._id ? "translate-x-0.5" : ""
                              }`} />
                            </div>
                          </div>
                        </button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`button-session-menu-${session._id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingSessionId(session._id);
                              setRenameValue(session.title);
                            }}
                            data-testid={`button-rename-session-${session._id}`}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteSessionId(session._id);
                            }}
                            data-testid={`button-delete-session-${session._id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
                );
              })()}
            </div>
          </ScrollArea>
        </aside>
        
        {/* Center Pane - Session Details / Transcription */}
        <main className={`flex-1 overflow-hidden bg-background ${selectedSessionId ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}>
          <SessionPaneErrorBoundary>
          {sessionUnavailable ? (
            <div
              className="h-full flex flex-col items-center justify-center text-center px-6"
              data-testid="state-session-unavailable"
            >
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                <FileText className="w-10 h-10 text-destructive" />
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                Session not found or no access
              </h2>
              <p className="text-muted-foreground max-w-md mb-6">
                This session doesn&apos;t exist or you don&apos;t have permission to view it. Returning you to your dashboard.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                data-testid="button-back-to-dashboard"
              >
                Back to dashboard
              </Button>
            </div>
          ) : selectedSession ? (
            <div className="h-full flex flex-col">
              <SessionViewLogger
                userId={convexUser._id}
                sessionId={selectedSession._id as Id<"recordingSessions">}
              />
              {/* Session Header */}
              <div className="p-4 md:p-6 border-b border-border bg-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="md:hidden shrink-0"
                        disabled={isRecordingActive}
                        onClick={() => {
                          if (isRecordingActive) {
                            toast({
                              title: "Recording in progress",
                              description: "Please stop the current recording before navigating away.",
                              variant: "destructive",
                            });
                            return;
                          }
                          setSelectedSessionId(null);
                        }}
                        data-testid="button-back-to-sessions"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </Button>
                      {editingCenterTitle ? (
                        <input
                          autoFocus
                          value={centerTitleValue}
                          onChange={(e) => setCenterTitleValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRenameSession(selectedSession._id as Id<"recordingSessions">, centerTitleValue);
                            if (e.key === "Escape") setEditingCenterTitle(false);
                          }}
                          onBlur={() => handleRenameSession(selectedSession._id as Id<"recordingSessions">, centerTitleValue)}
                          className="text-xl md:text-2xl font-heading font-bold text-foreground bg-background border border-primary/30 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-primary w-full max-w-md"
                          data-testid="input-rename-center-title"
                        />
                      ) : (
                        <h1
                          className="text-xl md:text-2xl font-heading font-bold text-foreground flex items-center gap-2 group/title cursor-pointer"
                          onClick={() => {
                            setEditingCenterTitle(true);
                            setCenterTitleValue(selectedSession.title);
                          }}
                          data-testid="text-session-title"
                        >
                          {selectedSession.title}
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                        </h1>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {selectedSession.patientName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(selectedSession.recordingDate), "MMMM d, yyyy 'at' h:mm a")}
                      </span>
                      {sessionTemplate && selectedSession.templateId && (
                        <Link
                          href={`/templates/${selectedSession.templateId}`}
                          className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
                          data-testid="link-session-template"
                        >
                          <FileText className="w-4 h-4" />
                          {sessionTemplate.name}
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant={sideChatOpen ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSideChatOpen((v) => !v)}
                      className="gap-2 hidden lg:inline-flex"
                      data-testid="button-toggle-side-chat"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Side Chat
                    </Button>
                    <ShareSessionDialog
                      userId={convexUser._id}
                      sessionId={selectedSession._id as Id<"recordingSessions">}
                    />
                    <AuditViewerDialog
                      userId={convexUser._id}
                      sessionId={selectedSession._id as Id<"recordingSessions">}
                    />
                    <EditSessionDialog 
                      session={selectedSession as RecordingSession} 
                      userId={convexUser._id} 
                      onSuccess={() => {}}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteSessionId(selectedSession._id)}
                      data-testid="button-delete-session"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <StatusBadge 
                      status={selectedSession.status}
                      hasTranscription={!!selectedSession.transcription}
                      hasGeneratedDocument={!!selectedSession.generatedDocument}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-hidden">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                  <div className="border-b border-border px-6 pt-2">
                    <TabsList className="bg-transparent h-auto p-0 gap-4" data-testid="tabs-session-detail">
                      <TabsTrigger 
                        value="document" 
                        className="bg-transparent px-1 pb-2 pt-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
                        data-testid="tab-document"
                      >
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        Document
                      </TabsTrigger>
                      <TabsTrigger 
                        value="notes" 
                        className="bg-transparent px-1 pb-2 pt-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
                        data-testid="tab-notes"
                      >
                        <Pencil className="w-4 h-4 mr-1.5" />
                        Notes
                      </TabsTrigger>
                      <TabsTrigger 
                        value="transcript" 
                        className="bg-transparent px-1 pb-2 pt-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
                        data-testid="tab-transcript"
                      >
                        <FileText className="w-4 h-4 mr-1.5" />
                        Transcript
                      </TabsTrigger>
                      <TabsTrigger
                        value="speakers"
                        className="bg-transparent px-1 pb-2 pt-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
                        data-testid="tab-speakers"
                      >
                        <Users className="w-4 h-4 mr-1.5" />
                        Speakers
                      </TabsTrigger>
                      {isExperimental && (
                        <TabsTrigger
                          value="transcript-experimental"
                          className="bg-transparent px-1 pb-2 pt-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
                          data-testid="tab-transcript-experimental"
                        >
                          <FlaskConical className="w-4 h-4 mr-1.5" />
                          Transcription - Experimental
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </div>

                  <TabsContent value="document" className="flex-1 overflow-auto m-0 p-6">
                    <div className="max-w-3xl">
                      <div className="flex items-center gap-3 mb-4">
                        <Label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Template:</Label>
                        <Select
                          value={selectedSession.templateId || "none"}
                          onValueChange={async (value) => {
                            const newTemplateId = value !== "none" ? value as Id<"templates"> : undefined;
                            try {
                              await updateSessionMutation({
                                id: selectedSession._id,
                                userId: convexUser._id,
                                templateId: newTemplateId,
                                clearTemplateId: value === "none",
                              });
                            } catch {
                              toast({ title: "Error", description: "Failed to update template.", variant: "destructive" });
                            }
                          }}
                        >
                          <SelectTrigger className="w-[250px]" data-testid="select-document-template">
                            <SelectValue placeholder="Select a template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No template</SelectItem>
                            {templates?.map((t) => (
                              <SelectItem key={t._id} value={t._id} data-testid={`option-template-${t._id}`}>
                                {t.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {((selectedSession.transcription || selectedSession.notes) && selectedSession.templateId) || (selectedSession.generatedDocument && selectedSession.templateId) ? (
                        <DocumentGenerator
                          transcription={selectedSession.transcription}
                          notes={selectedSession.notes}
                          templateId={selectedSession.templateId}
                          patientName={selectedSession.patientName}
                          gender={selectedSession.gender}
                          sessionTitle={selectedSession.title}
                          sessionId={selectedSession._id}
                          userId={convexUser._id}
                          existingDocument={selectedSession.generatedDocument}
                          existingDocumentGeneratedAt={selectedSession.documentGeneratedAt}
                          existingModelUsed={selectedSession.modelUsed}
                          preferredModel={convexUser.preferredModel}
                          preferredModelSetAt={(convexUser as any).preferredModelSetAt}
                          onDocumentGenerated={(hasDoc) => setHasGeneratedDocument(hasDoc)}
                          autoGenerate={autoGenerateRequested}
                          onAutoGenerateConsumed={() => setAutoGenerateRequested(false)}
                          defaultDocView={defaultDocView}
                        />
                      ) : autoGenerateRequested && selectedSession.status !== "failed" ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="document-queue-status">
                          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                            <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                          </div>
                          <h3 className="text-lg font-medium text-foreground mb-2">
                            Preparing Document Generation…
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm">
                            Waiting for transcription to complete. Your document will be generated automatically once the transcript is ready.
                          </p>
                        </div>
                      ) : selectedSession.status === "processing" && selectedSession.templateId ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="document-processing-status">
                          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                            <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                          </div>
                          <h3 className="text-lg font-medium text-foreground mb-2">
                            Transcription In Progress…
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm">
                            Waiting for transcription to complete before a document can be generated.
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="document-empty-state">
                          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                            <Sparkles className="w-8 h-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-medium text-foreground mb-2">
                            No Document Yet
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm">
                            {!selectedSession.templateId 
                              ? "Select a template above to generate a structured document."
                              : "Add a transcription or notes to this session, then generate a document using the assigned template."}
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="notes" className="data-[state=active]:flex-1 overflow-hidden m-0 p-6 data-[state=active]:flex flex-col min-h-0">
                    <NotesEditor
                      sessionId={selectedSession._id}
                      userId={convexUser._id}
                      notes={selectedSession.notes}
                      onSaved={() => triggerSpeakerAttribution(selectedSession._id)}
                      speakerAttributionStatus={selectedSession.speakerAttributionStatus}
                      hasAttributionResult={
                        !!selectedSession.speakerAttribution &&
                        selectedSession.speakerAttributionStatus === "done"
                      }
                      onViewSpeakers={() => {
                        setActiveTab("speakers");
                      }}
                      onRetryAttribution={() => triggerSpeakerAttribution(selectedSession._id)}
                      onLabelSpeakers={() => triggerSpeakerAttribution(selectedSession._id)}
                      isAttributing={isAttributing}
                    />
                  </TabsContent>

                  <TabsContent value="transcript" className="flex-1 overflow-auto m-0 p-6">
                    <div className="max-w-3xl">
                      {(() => {
                        const showRecorder = true;
                        return (
                        <>

                          {selectedSession.transcription && (
                            <div className="relative mb-8">
                              <div className="absolute top-0 right-0 flex items-center gap-1">
                                {!selectedSession.speakerAttributionStatus && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => triggerSpeakerAttribution(selectedSession._id)}
                                    disabled={isAttributing}
                                    data-testid="button-label-speakers"
                                  >
                                    {isAttributing ? (
                                      <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Labeling…</>
                                    ) : (
                                      <><Users className="w-3 h-3 mr-1.5" />Label Speakers</>
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover-elevate"
                                  onClick={() => handleCopyTranscript(selectedSession.transcription || "")}
                                  aria-label="Copy transcript"
                                  title="Copy transcript"
                                  data-testid="button-copy-transcript"
                                >
                                  {transcriptCopied ? (
                                    <Check className="w-4 h-4" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                              <div className="pr-20">
                                <SpeakerAttributedTranscript
                                  transcription={selectedSession.transcription}
                                  attributionStatus={selectedSession.speakerAttributionStatus}
                                  attributionJson={selectedSession.speakerAttribution}
                                  onTriggerAttribution={
                                    selectedSession.speakerAttributionStatus === "failed"
                                      ? () => triggerSpeakerAttribution(selectedSession._id)
                                      : undefined
                                  }
                                  initialMode={transcriptInitialMode}
                                />
                              </div>
                            </div>
                          )}
                          
                          {selectedSession.status === "failed" && !selectedSession.generatedDocument && (
                            <div className="flex flex-col items-center justify-center py-8 text-center mb-6 bg-destructive/5 rounded-lg">
                              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-3">
                                <AlertCircle className="w-6 h-6 text-destructive" />
                              </div>
                              <h3 className="text-base font-medium text-foreground mb-1">
                                {(selectedSession.audioData || selectedSession.audioUrl) ? "Transcription Failed" : "Last Recording Failed"}
                              </h3>
                              <p className="text-sm text-muted-foreground max-w-sm">
                                {(selectedSession.audioData || selectedSession.audioUrl)
                                  ? "Your audio was saved. You can retry transcription or record new audio."
                                  : "There was an error with the previous recording. Try again below."}
                              </p>
                              {selectedSession.audioApiRecordingId && (
                                <RetryTranscription
                                  sessionId={selectedSession._id}
                                  userId={convexUser._id}
                                  audioApiRecordingId={selectedSession.audioApiRecordingId}
                                  gatewaySessionId={selectedSession.gatewaySessionId}
                                  existingTranscription={selectedSession.transcription}
                                  onTranscriptionComplete={() => triggerSpeakerAttribution(selectedSession._id)}
                                />
                              )}
                            </div>
                          )}
                          
                          {showRecorder && (
                          <div className={selectedSession.transcription ? "pt-6 border-t border-border" : ""}>
                            <h3 className="text-sm font-semibold text-foreground mb-4">
                              {selectedSession.transcription ? "Continue Recording" : "Start Recording"}
                            </h3>
                            <AudioRecorder 
                              sessionId={selectedSession._id}
                              userId={convexUser._id}
                              existingTranscription={selectedSession.transcription}
                              sessionStatus={selectedSession.status}
                              onTranscriptionComplete={() => triggerSpeakerAttribution(selectedSession._id)}
                              onEndSession={() => {
                                if (selectedSession.templateId) {
                                  setAutoGenerateRequested(true);
                                  setActiveTab("document");
                                } else if (templates && templates.length > 0) {
                                  const defaultTemplate = templates[0];
                                  updateSessionMutation({
                                    id: selectedSession._id,
                                    userId: convexUser._id,
                                    templateId: defaultTemplate._id,
                                  }).then(() => {
                                    setAutoGenerateRequested(true);
                                    setActiveTab("document");
                                  }).catch((err) => {
                                    console.error("Failed to assign default template:", err);
                                  });
                                }
                              }}
                            />
                          </div>
                          )}
                        </>
                        );
                      })()}
                    </div>
                  </TabsContent>

                  <TabsContent value="speakers" className="flex-1 overflow-auto m-0 p-6" data-testid="content-speakers">
                    <div className="max-w-3xl">
                      <SpeakerAttributionViewer
                        attributionStatus={selectedSession.speakerAttributionStatus}
                        attributionJson={selectedSession.speakerAttribution}
                      />
                    </div>
                  </TabsContent>

                  {isExperimental && (
                    <TabsContent value="transcript-experimental" className="flex-1 overflow-auto m-0 p-6" data-testid="content-transcript-experimental">
                      <ExperimentalRecorder
                        templateSections={experimentalTemplateSections ?? undefined}
                        sessionTitle={selectedSession.title}
                        sessionId={selectedSession._id}
                        userId={convexUser._id}
                        templateId={selectedSession.templateId}
                        onDocumentSaved={() => setActiveTab("document")}
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-6">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                Select a Recording Session
              </h2>
              <p className="text-muted-foreground max-w-md">
                Choose a session from the list on the left to view the transcription details and patient information.
              </p>
            </div>
          )}
          </SessionPaneErrorBoundary>
        </main>

        {selectedSession && sideChatOpen && (
          <SideChat
            sessionId={selectedSession._id}
            transcription={selectedSession.transcription || ""}
            notes={selectedSession.notes || ""}
            generatedDocument={selectedSession.generatedDocument || ""}
            onClose={() => setSideChatOpen(false)}
          />
        )}
      </div>

      <AlertDialog open={!!deleteSessionId} onOpenChange={(open) => !open && setDeleteSessionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recording Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this recording session? This action cannot be undone and all transcription data will be permanently lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSession}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
